import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { UNSAFE_NavigationContext as NavigationContext, Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../../lib/hooks';
import { notifications } from '../../../lib/notifications';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import PhotoBoothInbox from '../../../components/common/PhotoBoothInbox';
import { useIncomeExpenseStore } from '../../../store/incomeExpenseStore';
import imageGenerationService from '../../../services/imageGenerationService';
import usageService from '../../../services/usageService';
import { buildPhotoDisplayName } from '../../../utils/photoFileName';
import {
    IconUpload,
    IconX,
    IconDownload,
    IconPalette,
    IconPlus,
    IconQrcode,
    IconCheck,
    IconEraser,
    IconRefresh,
    IconPhoto,
    IconUserSquare,
    IconFriends,
    IconNfc,
    IconPlaneTilt,
    IconSun,
    IconContrast,
    IconDroplet,
    IconCopy,
    IconPrinter,
    IconRepeat,
    IconChevronLeft,
    IconChevronRight,
    IconSparkles,
    IconHourglass,
    IconRotate,
    IconRotateClockwise,
    IconRotate2,
    IconZoomIn,
    IconZoomOut,
    IconArrowsExchange,
    IconDeviceFloppy,
    IconExternalLink,
    IconEdit,
    IconPhotoUp,
    IconUsers,
    IconArrowsMaximize,
    IconUserScan,
} from '@tabler/icons-react';
import QRCode from 'qrcode';
import QrUploadModal from '@/components/common/QrUploadModal';
import qrSessionService from '../../../services/qrSessionService';
import ImagePreviewModal from '../../../components/common/ImagePreviewModal';
import LowCreditBanner from '../../../components/common/LowCreditBanner';
import PrintModal from './PrintModal';
import CustomDownloadModal from './CustomDownloadModal';
import styles from './GenerateImage.module.scss';
import PhotoEditDrawer from '../../../components/common/PhotoEditDrawer';
import { ActionBtn, EditToggle, InfoButton, PrintButton, btnIcon } from '../../../components/common/PhotoActionButtons';
import { NEUTRAL_EDITS, bakePhotoEdits, isEdited, useFilterThumbs, usePhotoEditPreview } from '../../../utils/photoEdits';
import { copyImageToClipboard } from '../../../utils/imageShare';
import { sanitizeText } from '../../../utils/sanitize';

const RENAME_MAX = 120; // a saved photo's name (Image info → rename)
import { VISA_GROUPS } from './VisaCountryModal';
import EraserModal from './EraserModal';
import CropStage from './CropStage';
import ObjectAdjustPanel from './ObjectAdjustPanel';
import { runFaceEnhance } from './faceEnhanceEngine';
import { fitsOutputLimit, readImageDimensions, upscaleImageFile } from '../tools/image/upscale/upscaleImageEngine';
import {
    parseBgToken, isSpecialBgToken, specialBgCss, paintSpecialBg,
    drawImageCover, presetSrc, BG_PRESETS,
} from './bgPresets';

// ─── Tool-card icons (inline SVG, no external deps) ─────────────────────────────
// A checkerboard rounded square, the universal "transparent / no background" glyph.
// `id` must be unique per render site (used for the rounded-corner clip path).
function CheckerIcon({ size = 34, id }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <defs>
                <clipPath id={`${id}-clip`}><rect x="3" y="3" width="18" height="18" rx="4.5" /></clipPath>
            </defs>
            <g clipPath={`url(#${id}-clip)`}>
                <rect x="3" y="3" width="18" height="18" fill="#ffffff" />
                <g fill="#cbd5e1">
                    <rect x="3" y="3" width="6" height="6" />
                    <rect x="15" y="3" width="6" height="6" />
                    <rect x="9" y="9" width="6" height="6" />
                    <rect x="3" y="15" width="6" height="6" />
                    <rect x="15" y="15" width="6" height="6" />
                </g>
            </g>
            <rect x="3" y="3" width="18" height="18" rx="4.5" fill="none" stroke="#94a3b8" strokeWidth="1.4" />
        </svg>
    );
}

// ─── Photo size configs ───────────────────────────────────────────────────────
const PHOTO_SIZES_CONFIG = {
    bd_passport:  { wmm: 45, hmm: 55, exportW: 832,  exportH: 1024 },
    bd_epassport: { wmm: 35, hmm: 45, exportW: 650,  exportH: 836  },
    bd_visa:      { wmm: 51, hmm: 51, exportW: 945,  exportH: 945  },
    visa_2x2:     { wmm: 51, hmm: 51, exportW: 945,  exportH: 945  },
    visa_35x45:   { wmm: 35, hmm: 45, exportW: 650,  exportH: 836  },
    visa_40x60:   { wmm: 40, hmm: 60, exportW: 743,  exportH: 1117 },
    visa_40x50:   { wmm: 40, hmm: 50, exportW: 743,  exportH: 930  },
    visa_40x40:   { wmm: 40, hmm: 40, exportW: 743,  exportH: 743  },
    visa_50x50:   { wmm: 50, hmm: 50, exportW: 930,  exportH: 930  },
    visa_50x70:   { wmm: 50, hmm: 70, exportW: 930,  exportH: 1303 },
    visa_33x48:   { wmm: 33, hmm: 48, exportW: 614,  exportH: 893  },
    visa_35x50:   { wmm: 35, hmm: 50, exportW: 650,  exportH: 930  },
    visa_36x47:   { wmm: 36, hmm: 47, exportW: 669,  exportH: 874  },
    visa_38x48:   { wmm: 38, hmm: 48, exportW: 707,  exportH: 893  },
    visa_50x60:   { wmm: 50, hmm: 60, exportW: 930,  exportH: 1117 },
    bd_birth:     { wmm: 33, hmm: 48, exportW: 614,  exportH: 893  },
    dual_image:   { wmm: 50, hmm: 41, exportW: 1249, exportH: 1024 }, // replaced by the Settings dual size (dualSizeConfig)

    // ── Print paper sizes (portrait) — frame aspect matches the paper, ~150 DPI export ──
    print_2r:       { wmm: 64,  hmm: 89,  exportW: 378,  exportH: 526  },
    print_3r:       { wmm: 89,  hmm: 127, exportW: 526,  exportH: 750  },
    print_4r:       { wmm: 102, hmm: 152, exportW: 602,  exportH: 898  },
    print_5r:       { wmm: 127, hmm: 178, exportW: 750,  exportH: 1051 },
    print_6r:       { wmm: 152, hmm: 203, exportW: 898,  exportH: 1199 },
    print_8r:       { wmm: 203, hmm: 254, exportW: 1199, exportH: 1500 },
    print_a4:       { wmm: 210, hmm: 297, exportW: 1240, exportH: 1754 },
    print_letter:   { wmm: 216, hmm: 279, exportW: 1276, exportH: 1648 },
    // ── Print paper sizes (landscape) — width/height swapped ──
    print_2r_l:     { wmm: 89,  hmm: 64,  exportW: 526,  exportH: 378  },
    print_3r_l:     { wmm: 127, hmm: 89,  exportW: 750,  exportH: 526  },
    print_4r_l:     { wmm: 152, hmm: 102, exportW: 898,  exportH: 602  },
    print_5r_l:     { wmm: 178, hmm: 127, exportW: 1051, exportH: 750  },
    print_6r_l:     { wmm: 203, hmm: 152, exportW: 1199, exportH: 898  },
    print_8r_l:     { wmm: 254, hmm: 203, exportW: 1500, exportH: 1199 },
    print_a4_l:     { wmm: 297, hmm: 210, exportW: 1754, exportH: 1240 },
    print_letter_l: { wmm: 279, hmm: 216, exportW: 1648, exportH: 1276 },
};

// The dual photo takes the dual print size from Settings (default 50×41), so the print window prints it at that size with nothing cut off.
const dualSizeConfig = (printSizes = {}) => {
    const wmm = Number(printSizes.passportDualW) || 50;
    const hmm = Number(printSizes.passportDualH) || 41;
    return { wmm, hmm, exportW: Math.round(1024 * wmm / hmm), exportH: 1024 };
};

// Print/paper sizes shown in the "ছবির মাপ" selector. `base` is the portrait
// key in PHOTO_SIZES_CONFIG; the landscape variant is `${base}_l`.
const PRINT_SIZES = [
    { base: 'print_2r',     label: '2R',     sub: '2×3″'      },
    { base: 'print_3r',     label: '3R',     sub: '3.5×5″'    },
    { base: 'print_4r',     label: '4R',     sub: '4×6″'      },
    { base: 'print_5r',     label: '5R',     sub: '5×7″'      },
    { base: 'print_6r',     label: '6R',     sub: '6×8″'      },
    { base: 'print_8r',     label: '8R',     sub: '8×10″'     },
    { base: 'print_a4',     label: 'A4',     sub: '210×297mm' },
    { base: 'print_a4_l',   label: 'A4 ↔',   sub: '297×210mm' },
    { base: 'print_letter', label: 'Letter', sub: '8.5×11″'   },
];

// A small paper/frame glyph drawn to the real aspect ratio of the given size,
// so each print size card looks distinct instead of a repeated printer icon.
function PaperGlyph({ wmm, hmm, document = false }) {
    const box = 26, pad = 3, max = box - pad * 2;
    const ratio = wmm / hmm;
    const w = ratio >= 1 ? max : max * ratio;
    const h = ratio >= 1 ? max / ratio : max;
    const x = (box - w) / 2, y = (box - h) / 2;
    return (
        <svg width="28" height="28" viewBox={`0 0 ${box} ${box}`} fill="none">
            <rect x={x} y={y} width={w} height={h} rx="2" stroke="currentColor" strokeWidth="1.8" />
            {document && (
                <>
                    <line x1={x + 3} y1={y + h * 0.32} x2={x + w - 3} y2={y + h * 0.32} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    <line x1={x + 3} y1={y + h * 0.5}  x2={x + w - 3} y2={y + h * 0.5}  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    <line x1={x + 3} y1={y + h * 0.68} x2={x + w - 5} y2={y + h * 0.68} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </>
            )}
        </svg>
    );
}

const BG_HEX = {
    white:     '#ffffff',
    blue:      '#2196F3',
    sky_blue:  '#03A9F4',
    grey:      '#9E9E9E',
    dark_blue: '#3068E1',
    teal:      '#00897B',
    off_white: '#FFF8DC',
};

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

// Preview zoom bounds, as a scale factor. Named because the slider, the ±10%
// buttons, the history restore and the eraser hand-back all have to agree —
// the limit used to be written out by hand in each of those places.
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 4;

// Selection-brush loupe: diameter on screen, and how much it magnifies what is
// already displayed. 3× is enough to see individual hairs at the frame's size
// without the view turning to mush.
const LOUPE_SIZE = 148;
const LOUPE_ZOOM = 3;

// Eases the ±10% / ±90° toolbar steps. Kept as a constant because a drag turns
// it off inline and has to put back the exact same string — React will not
// restore it on the next render, since its own copy of the style never changed.
const PREVIEW_TRANSITION = 'transform 140ms cubic-bezier(0.22, 1, 0.36, 1), filter 0.3s ease';

// The client-side background-removal library can't decode AVIF, so an AVIF upload
// fails deep inside the worker ("Invalid format: image/avif"). Reject it up front
// with a clear message instead of letting the user hit that cryptic error later.
const isUnsupportedUpload = (file) => {
    if (!file) return false;
    const type = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();
    return type === 'image/avif' || name.endsWith('.avif');
};

// Resize an image file so neither dimension exceeds maxDim.
// Returns a new File if resize was needed, or the original File if already small enough.
//
// 4096 rather than a tighter number: a 12MP phone photo is 3024×4032, so the
// common case now passes through untouched — no resample, no JPEG re-encode, the
// user's own bytes go into the crop. The cap still exists so a 50MP or scanned
// TIFF-sized source cannot blow up the background-removal worker's memory.
const MAX_SOURCE_DIM = 4096;
function resizeIfNeeded(file, maxDim = MAX_SOURCE_DIM) {
    return new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new window.Image();
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            const { naturalWidth: w, naturalHeight: h } = img;
            if (w <= maxDim && h <= maxDim) { resolve(file); return; }
            const scale = Math.min(maxDim / w, maxDim / h);
            const canvas = document.createElement('canvas');
            canvas.width  = Math.round(w * scale);
            canvas.height = Math.round(h * scale);
            canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
            const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
            canvas.toBlob(
                (blob) => resolve(blob ? new File([blob], file.name, { type: mime }) : file),
                mime,
                mime === 'image/jpeg' ? 0.92 : undefined
            );
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); resolve(file); };
        img.src = objectUrl;
    });
}
let historyIdCounter = 0;

// Daily save counter — resets when date changes, gives "Manual Edit 2026-06-15 #1" style names
const _dailyCounter = { date: null, count: 0 };
const getManualEditSaveName = () => {
    const today = new Date().toISOString().slice(0, 10);
    if (_dailyCounter.date !== today) { _dailyCounter.date = today; _dailyCounter.count = 0; }
    _dailyCounter.count += 1;
    return `Manual Edit ${today} #${_dailyCounter.count}`;
};

// ─────────────────────────────────────────────────────────────────────────────
export default function GenerateImageManual() {
    const { t, i18n } = useTranslation();
    const isBn = i18n.language === 'bn';
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { selectedOfficeSpace: rawSelectedOfficeSpace } = useOfficeSpaceStore();
    const { user, getCurrentUser } = useAuthStore();
    const { openModal: openIncomeExpenseModal } = useIncomeExpenseStore();
    const isAdmin = user?.role === 'admin';
    // Photo Booth on/off for this office space (null = still resolving). While the
    // booth is active the sidebar QR-transfer card is redundant — customers send
    // photos through the booth — so it only shows once the booth resolves to OFF
    // (mirrors the generate-image page's pcResolved/pcEnabled behaviour).
    const [boothActive, setBoothActive] = useState(null);
    const selectedOfficeSpace = isAdmin ? null : rawSelectedOfficeSpace;
    const [usageStats, setUsageStats] = useState(null);

    const [previewUrl, setPreviewUrl]     = useState(null);
    const [processedUrl, setProcessedUrl] = useState(null);
    const [bgRemoved, setBgRemoved]       = useState(false);
    const [isRemovingBg, setIsRemovingBg] = useState(false);
    const bgProgressRef      = useRef(null); // DOM ref — avoids re-renders that reset CSS animation
    const bgProgressLabelRef = useRef(null); // second ref for scanner overlay label
    const [isDraggingOver, setIsDraggingOver] = useState(false);

    const [size, setSize]                       = useState('bd_passport');
    // Free-size mode: the frame + export follow the loaded image's natural aspect
    // (no fixed paper ratio). Updated whenever a free-size image loads.
    const [freeAspect, setFreeAspect]           = useState(4 / 3);
    const [bg, setBg]                           = useState('');
    const [customColor, setCustomColor]         = useState('#ffffff');
    // Source url for image-based backgrounds (preset path or uploaded blob url)
    const [bgImageSrc, setBgImageSrc]           = useState('');
    const [visaPopoverOpen, setVisaPopoverOpen] = useState(false);
    const [printPopoverOpen, setPrintPopoverOpen] = useState(false);
    const [visaCountryLabel, setVisaCountryLabel] = useState('');
    const [presetBgOpen, setPresetBgOpen] = useState(false);

    const [brightness, setBrightness] = useState(100);
    const [contrast, setContrast]     = useState(100);
    const [saturation, setSaturation] = useState(100);
    const [sharp, setSharp]           = useState(0);
    const [isFaceEnhancing, setIsFaceEnhancing] = useState(false); // AI face restore (GFPGAN, client-side) — renders inline in preview
    const [isUpscaling, setIsUpscaling] = useState(false); // 4× super-resolution (Real-ESRGAN, client-side)
    const [faceSelectMode, setFaceSelectMode]   = useState(false); // manual face-area selection overlay
    const [faceRect, setFaceRect]               = useState(null);  // selection box in frame-display px {x,y,w,h}
    const faceDragRef = useRef(null);
    const [autoEnhanced, setAutoEnhanced]     = useState(false);
    const [preEnhanceVals, setPreEnhanceVals] = useState(null); // {brightness,contrast,saturation}

    // Print modal
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [printBlobUrl, setPrintBlobUrl]     = useState(null);
    // Record id backing the print (only when printing a SAVED image directly — e.g.
    // from the history preview). Lets PrintModal recover the stored background token
    // for a transparent cut-out. Null when the print blob already has the bg baked in
    // (the editor's own render path), so no lookup is needed.
    const [printImageId, setPrintImageId]     = useState(null);
    // Whether the image being printed is a free-size photo → PrintModal should open it
    // FREE (own aspect, one copy) instead of the default 4-up passport layout. Captured
    // per print launch so history prints use the image's own size, not the editor's.
    const [printIsFree, setPrintIsFree]       = useState(false);

    // Action loading states
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPrinting, setIsPrinting]       = useState(false);
    const [isSaving, setIsSaving]           = useState(false);

    // Custom download modal
    const [showCustomDownload, setShowCustomDownload]       = useState(false);
    const [customDownloadImageUrl, setCustomDownloadImageUrl] = useState(null);
    const [customDownloadImageId, setCustomDownloadImageId]   = useState(null);
    const [customDownloadImageBg, setCustomDownloadImageBg]   = useState(null);

    const [zoom, setZoom]         = useState(1);
    const [offset, setOffset]     = useState({ x: 0, y: 0 });
    const [rotation, setRotation] = useState(0); // degrees

    // Crop / rotate stage shown on upload (same full-screen CropStage the AI editor
    // uses). Locked to the selected photo-size aspect; output kept high-res for print.
    const [showCropModal, setShowCropModal] = useState(false);
    const [cropImageSrc, setCropImageSrc]   = useState(null);
    const [pendingCropName, setPendingCropName] = useState('');
    const [cropCfg, setCropCfg] = useState({ ratio: 45 / 55, outW: 1636, outH: 2000 });

    // Save to history state
    const [savedToHistory, setSavedToHistory] = useState(false);
    useEffect(() => { setSavedToHistory(false); }, [bg]);

    // Current edit tracking — null = create mode, string id = update mode
    // Manual-edit records arrive with source-tagged ids from the merged history
    // API ('m156' = ManualEditedImage row 156). Update mode tracks the NUMERIC
    // row id (what /update-manual/:id expects); the tag is routing/display-only.
    // Number('m156') is NaN — that silently disabled update mode and made every
    // save create a fresh record.
    const manualIdNum = (id) => {
        const n = parseInt(String(id ?? '').replace(/^m/, ''), 10);
        return Number.isFinite(n) ? n : null;
    };
    const [currentEditId, setCurrentEditId] = useState(null);
    const [savedImageInfo, setSavedImageInfo] = useState(null); // { id, aiImage, size, background, savedAt }
    // File name of the saved record — shown in the sidebar info box; double-click to rename.
    const [savedFileName, setSavedFileName] = useState('');
    const [nameEditing, setNameEditing] = useState(false);
    const [nameInputVal, setNameInputVal] = useState('');
    // Serial for manual saves (device-local) — mirrors the AI editor without a
    // server round-trip: per-day while the date is in the name, one continuous
    // counter when the user turned the date off (সেটিংস → ছবির নাম).
    const nextManualSerial = () => {
        const includeDate = user?.settings?.photoNameSettings?.includeDate !== false;
        const key = `sohozkaj:manualNameSerial:${includeDate ? new Date().toISOString().slice(0, 10) : 'continuous'}`;
        let n = 1;
        try {
            n = (parseInt(localStorage.getItem(key), 10) || 0) + 1;
            localStorage.setItem(key, String(n));
        } catch { /* ignore */ }
        return n;
    };
    const handleSaveFileName = async () => {
        const name = sanitizeText(nameInputVal, { maxLen: RENAME_MAX }).trim();
        if (!savedImageInfo?.id || !name || name === savedFileName) { setNameEditing(false); return; }
        try {
            await imageGenerationService.updateImageName(savedImageInfo.id, name);
            setSavedFileName(name);
            setImageHistory(prev => prev.map(img => img.id === savedImageInfo.id ? { ...img, fileName: name } : img));
            notifications.show({ message: t('imageGeneration.imageNameUpdated'), color: 'green', autoClose: 2000 });
        } catch {
            notifications.show({ message: t('imageGeneration.renameFailed'), color: 'red' });
        } finally {
            setNameEditing(false);
        }
    };
    // After bg-removal we auto-save once; this flag defers the save until the
    // processed image/state has settled, then switches the editor to update mode.
    const [autoSavePending, setAutoSavePending] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Dual image mode
    const [mergeLeft, setMergeLeft]   = useState(null); // { url, offsetX, offsetY, zoom }
    const [mergeRight, setMergeRight] = useState(null);

    // History (server-backed)
    const [imageHistory, setImageHistory]       = useState([]);
    const [historyPage, setHistoryPage]         = useState(1);
    const [hasMoreHistory, setHasMoreHistory]   = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedHistoryImage, setSelectedHistoryImage] = useState(null);
    const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true, loop: false, align: 'start' });
    // Maps a history image id → its aspect ratio (w/h). All slider thumbnails share
    // ONE fixed height; the width is derived from the aspect so any image size fits.
    const [historyAspects, setHistoryAspects] = useState({});
    const HISTORY_CARD_H = 160;

    const scrollHistoryLeft  = () => emblaApi?.scrollPrev();
    const scrollHistoryRight = () => emblaApi?.scrollNext();

    // QR
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrStatus, setQrStatus]       = useState('idle');
    const [qrDataUrl, setQrDataUrl]     = useState(null);
    const [qrToken, setQrToken]         = useState(null);
    const [qrMobileUrl, setQrMobileUrl] = useState(null);
    const [qrTimeLeft, setQrTimeLeft]   = useState(300);

    // Loading skeleton shown in upload area while fetching from history
    const [isLoadingFromHistory, setIsLoadingFromHistory] = useState(false);

    // Responsive compact layout (< 1024px) — keeps the right panel in its normal
    // column form down to 1024px, matching the shared 3-column grid breakpoint.
    const [isCompact, setIsCompact] = useState(() => typeof window !== 'undefined' && window.innerWidth < 1024);
    useEffect(() => {
        const handler = () => setIsCompact(window.innerWidth < 1024);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Mobile bottom nav active drawer
    const [activeDrawer, setActiveDrawer] = useState(null); // 'size' | 'bg' | 'adjust' | 'tools' | null

    // Feather eraser modal
    // Object tool — client-side Face / Skin / Hair detection + per-region adjust.
    // Lives inline in the sidebar; while open it drives a live preview that
    // replaces the working image in the photo frame (nothing else sees it).
    const [objectPanelOpen, setObjectPanelOpen] = useState(false);
    const [objectPreviewUrl, setObjectPreviewUrl] = useState(null);
    // Set by ObjectAdjustPanel while its selection brush is armed:
    // { mode, size, onStroke }. The photo lives in this component's frame, so
    // the capture surface has to be drawn here.
    const [objectBrush, setObjectBrush] = useState(null);
    const brushStrokeRef = useRef(null);
    const brushCursorRef = useRef(null);
    const loupeRef = useRef(null);

    const [eraserModalOpen, setEraserModalOpen] = useState(false);
    const [dualEraserUrl, setDualEraserUrl] = useState(null); // merged composite URL for dual mode in EraserModal
    // Saved position from last eraser session (zoom/offset within EraserModal frame)
    const [eraserZoom, setEraserZoom]                 = useState(null);
    const [eraserOffset, setEraserOffset]             = useState(null);
    const [eraserStartFromSaved, setEraserStartFromSaved] = useState(false);

    // Unsaved-changes navigation blocker (shown for back/forward navigation)
    const [showDiscardModal, setShowDiscardModal] = useState(false);

    // Auto-load image passed via navigation state (e.g. from "Reopen" in GeneratedDocuments)
    const location = useLocation();
    useEffect(() => {
        const { imageId, imageUrl, historyItem } = location.state || {};
        if (!imageId && !imageUrl && !historyItem) return;
        window.history.replaceState({}, '');

        const load = async () => {
            try {
                if (historyItem) {
                    // Full restore — same flow as clicking the history carousel
                    setIsLoadingFromHistory(true);
                    const blob = await imageGenerationService.downloadImage(historyItem.id);
                    const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
                    pendingHistoryMetaRef.current = {
                        background: historyItem.background,
                        size: historyItem.size,
                        zoom: historyItem.zoom ?? null,
                        offsetX: historyItem.offsetX ?? null,
                        offsetY: historyItem.offsetY ?? null,
                        manualBgRemoved: historyItem.manualBgRemoved ?? false,
                    };
                    setCurrentEditId(manualIdNum(historyItem.id));
                    setSavedImageInfo({ id: historyItem.id, aiImage: historyItem.aiImage, size: historyItem.size, background: historyItem.background, savedAt: null });
                    setSavedFileName(historyItem.fileName || '');
                    setSavedToHistory(true);
                    skipBlockerRef.current = true;
                    navigate(`?id=${historyItem.id}`, { replace: true });
                    skipBlockerRef.current = false;
                    handleFileSelect(new File([blob], `image.${ext}`, { type: blob.type }), { fromHistory: true });
                    setIsLoadingFromHistory(false);
                    return;
                }
                let blob;
                if (imageId) {
                    // Use backend proxy to avoid CORS issues
                    blob = await imageGenerationService.downloadImage(imageId);
                } else {
                    const r = await fetch(imageUrl);
                    blob = await r.blob();
                }
                const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
                const file = new File([blob], `image.${ext}`, { type: blob.type });
                handleFileSelect(file);
            } catch (e) {
                console.error('Failed to load image for editing:', e);
                setIsLoadingFromHistory(false);
            }
        };
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // run once on mount

    // Read ?id= from URL on mount — enter update mode for that record
    useEffect(() => {
        const idFromUrl = searchParams.get('id');
        if (idFromUrl) {
            setCurrentEditId(manualIdNum(idFromUrl));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pendingNavRef   = useRef(null); // stores the popstate event's target URL
    const skipBlockerRef  = useRef(false); // set true during save-navigate to bypass the unsaved-changes blocker

    const fileInputRef   = useRef(null);
    const colorPickerRef = useRef(null);
    const cropStageRef   = useRef(null); // imperative handle to CropStage.apply()
    const pendingCropFileRef = useRef(null); // resized source held for crop skip/error
    const stageRef       = useRef(null);
    const frameRef       = useRef(null);
    const dragRef        = useRef(null);
    // ── Live view (zoom / rotation / pan) ──
    // Dragging a slider fires ~60 events a second, and putting each one through
    // setState re-renders this whole editor — panels, tool grids, history and
    // all — which is what made zoom and rotate feel sticky. During a drag the
    // values live in these refs and are written straight to the image's own
    // style; React state is committed once, when the drag ends.
    const previewImgRef    = useRef(null);
    const zoomSliderRef    = useRef(null);
    const rotationSliderRef = useRef(null);
    const zoomLabelRef     = useRef(null);
    const rotationLabelRef = useRef(null);
    const liveViewRef      = useRef({ zoom: 1, rotation: 0, x: 0, y: 0 });
    const viewDraggingRef  = useRef(false);
    const qrTimerRef     = useRef(null);
    const qrPollRef      = useRef(null);
    const inlineMergeLeftRef  = useRef(null);
    const inlineMergeRightRef = useRef(null);
    const leftZoneRef         = useRef(null);
    const rightZoneRef        = useRef(null);
    const dualDragRef         = useRef(null);
    const copyUrlRef          = useRef(null);
    const pendingHistoryMetaRef   = useRef(null);
    const pendingOffsetRestoreRef = useRef(null);

    const isDualMode   = size === 'dual_image';
    const getBgColor   = () => bg === 'custom' ? customColor : bg === 'transparent' ? 'transparent' : (BG_HEX[bg] || '#ffffff');
    // Visual background for UI panels — uses checkerboard for transparent instead of CSS transparent
    const getVisualBg  = () => bg === 'transparent' ? 'repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/10px 10px' : getBgColor();

    // CSS style for the frame preview — handles solid colors, gradient/pattern/preset
    // tokens, and uploaded image backgrounds.
    const frameBgStyle = () => {
        if (bg === 'transparent') return { background: 'repeating-conic-gradient(#b0b0b0 0% 25%, #e8e8e8 0% 50%) 0 0 / 20px 20px' };
        const d = parseBgToken(bg);
        if (d.kind === 'gradient' || d.kind === 'pattern' || d.kind === 'preset') return specialBgCss(bg) || { background: '#ffffff' };
        if (d.kind === 'image') return bgImageSrc ? { background: `center/cover no-repeat url(${bgImageSrc})` } : { background: '#ffffff' };
        return { background: getBgColor() };
    };

    // Paint the active background onto an export canvas (full rect). Async because
    // image-based backgrounds (preset/upload) must be loaded first.
    // Preset/CDN background URLs are also shown as CSS backgrounds (no CORS), which
    // caches a response without CORS headers — loading it again with crossOrigin then
    // fails and taints the export canvas. Fetch fresh in CORS mode and load from a
    // same-origin blob URL. Works for CDN, blob: and data: sources alike.
    const loadImageEl = async (src) => {
        const resp = await fetch(src, { mode: 'cors', cache: 'reload' });
        if (!resp.ok) throw new Error(`bg fetch failed: ${resp.status}`);
        const objUrl = URL.createObjectURL(await resp.blob());
        try {
            return await new Promise((res, rej) => {
                const i = new Image();
                i.onload = () => res(i); i.onerror = () => rej(new Error('bg decode failed'));
                i.src = objUrl;
            });
        } finally { setTimeout(() => URL.revokeObjectURL(objUrl), 5000); }
    };
    const paintBgOntoCtx = async (ctx, w, h) => {
        if (bg === 'transparent') return;
        const d = parseBgToken(bg);
        if (d.kind === 'gradient' || d.kind === 'pattern') { paintSpecialBg(ctx, 0, 0, w, h, bg); return; }
        if (d.kind === 'preset' || d.kind === 'image') {
            const src = d.kind === 'preset' ? presetSrc(d.id) : bgImageSrc;
            if (src) { try { const el = await loadImageEl(src); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h); drawImageCover(ctx, el, 0, 0, w, h); } catch { /* ignore load failure */ } }
            return;
        }
        ctx.fillStyle = getBgColor(); ctx.fillRect(0, 0, w, h);
    };
    // In dual mode, processedUrl holds the merged+bg-removed result once done
    const displayImage = isDualMode ? (processedUrl || null) : (processedUrl || previewUrl);

    // The photo editors' edit drawer (Studio Editor / AI photo editor) over the preview. Here — like the old colour sliders — the edits
    // shape the photo layer itself: previewed live and baked into download / print / copy / save (single photo only; not dual mode).
    const [edits, setEdits] = useState(NEUTRAL_EDITS);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editSource, setEditSource] = useState(null); // { url, blob } — the photo layer's pixels, fetched once per photo
    const editsActive = !isDualMode && isEdited(edits);
    useEffect(() => {
        if (isDualMode || !displayImage || (!editorOpen && !isEdited(edits)) || editSource?.url === displayImage) return undefined;
        let alive = true;
        fetch(displayImage).then((r) => r.blob()).then((blob) => { if (alive) setEditSource({ url: displayImage, blob }); }).catch(() => {});
        return () => { alive = false; };
    }, [displayImage, editorOpen, edits, isDualMode]); // eslint-disable-line react-hooks/exhaustive-deps
    const editBlob = editSource?.url === displayImage ? editSource.blob : null;
    const editedPreviewUrl = usePhotoEditPreview(editsActive ? editBlob : null, edits);
    const filterThumbs = useFilterThumbs(editorOpen ? editBlob : null);
    // a new photo starts unedited
    useEffect(() => { if (!previewUrl) { setEdits(NEUTRAL_EDITS); setEditorOpen(false); } }, [previewUrl]);
    useEffect(() => { if (isDualMode) setEditorOpen(false); }, [isDualMode]);
    // The photo layer for an export: the edits baked in at full size (a blob: URL the caller revokes), else the photo as it is.
    const getExportSourceUrl = async () => {
        if (!editsActive) return { url: displayImage, revoke: false };
        const blob = editBlob || await fetch(displayImage).then((r) => r.blob());
        const baked = await bakePhotoEdits(blob, edits, { type: 'image/png' });
        return { url: URL.createObjectURL(baked), revoke: true };
    };
    const sizeConfigs  = useMemo(() => ({ ...PHOTO_SIZES_CONFIG, dual_image: dualSizeConfig(user?.settings?.printSizes) }), [user?.settings?.printSizes]);
    const sizeConfig   = sizeConfigs[size] || PHOTO_SIZES_CONFIG.bd_passport;
    // "Free size" — no fixed paper ratio; crop is skipped and export keeps the
    // uploaded image's own aspect ratio.
    const isFreeSize   = size === 'free';
    // Output pixel size for the current size. Fixed sizes use their export config;
    // free size derives from the loaded image aspect at a ~2000px long edge.
    const getExportDims = () => {
        if (!isFreeSize) return { W: sizeConfig.exportW, H: sizeConfig.exportH };
        const a = freeAspect || (4 / 3);
        const LONG = 2000;
        return a >= 1
            ? { W: LONG, H: Math.max(1, Math.round(LONG / a)) }
            : { W: Math.max(1, Math.round(LONG * a)), H: LONG };
    };

    // ── File handling ─────────────────────────────────────────────────────────
    // Commit a ready-to-edit source image into the canvas (shared by fresh crops and
    // history reloads). Sets the preview and resets all per-image editor state.
    const commitSourceFile = useCallback((source, { fromHistory = false } = {}) => {
        const url = URL.createObjectURL(source);
        setPreviewUrl(url);
        setProcessedUrl(null);
        setBgRemoved(false);
        if (bgProgressRef.current)      bgProgressRef.current.textContent      = '';
        if (bgProgressLabelRef.current) bgProgressLabelRef.current.textContent = '';
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setRotation(0);
        setMergeLeft(null);
        setMergeRight(null);
        setAutoEnhanced(false);
        setPreEnhanceVals(null);
        if (!fromHistory) {
            // Fresh upload — exit update mode, clear saved info, clear URL param
            setCurrentEditId(null);
            setSavedImageInfo(null);
            setSavedFileName('');
            setNameEditing(false);
            setSavedToHistory(false);
            navigate('?', { replace: true });
        }
    }, [navigate]);

    const handleFileSelect = useCallback(async (file, { fromHistory = false } = {}) => {
        if (!file) return;
        // History/internal reloads are already-processed → straight to the canvas, no crop.
        if (fromHistory) { commitSourceFile(file, { fromHistory: true }); return; }
        // Reject AVIF — the bg-removal worker can't decode it.
        if (isUnsupportedUpload(file)) {
            notifications.show({ message: t('imageManualEditor.avifNotSupported'), color: 'red' });
            return;
        }
        // Resize oversized source images — 2500px max is more than enough for any passport export.
        const source = await resizeIfNeeded(file);
        // Decode-test before opening the modal so we never open it on an unreadable file.
        const testUrl = URL.createObjectURL(source);
        const ok = await new Promise((resolve) => {
            const img = new Image();
            img.onload  = () => { URL.revokeObjectURL(testUrl); resolve(true); };
            img.onerror = () => { URL.revokeObjectURL(testUrl); resolve(false); };
            img.src = testUrl;
        });
        if (!ok) {
            notifications.show({ message: t('imageManualEditor.imageLoadFailed'), color: 'orange' });
            commitSourceFile(source, { fromHistory: false });
            return;
        }
        // Free size → no crop needed; drop the whole (resized) image straight in.
        if (size === 'free') { commitSourceFile(source, { fromHistory: false }); return; }
        // Crop box locked to the selected photo-size aspect; output high-res (long
        // side 2000px) so the manual export never has to upscale.
        const ratio = sizeConfig.wmm / sizeConfig.hmm;
        const longSide = 2000;
        const outW = ratio >= 1 ? longSide : Math.round(longSide * ratio);
        const outH = ratio >= 1 ? Math.round(longSide / ratio) : longSide;
        setCropCfg({ ratio, outW, outH });
        pendingCropFileRef.current = source;
        const reader = new FileReader();
        reader.onload = (e) => {
            setCropImageSrc(e.target.result);
            setPendingCropName(source.name || file.name || 'photo.jpg');
            setShowCropModal(true);
        };
        reader.readAsDataURL(source);
    }, [commitSourceFile, sizeConfig, size, t]);

    // Free size: keep the preview frame + export aspect in sync with the loaded image.
    useEffect(() => {
        if (!isFreeSize || !displayImage) return;
        let cancelled = false;
        const im = new Image();
        im.onload = () => { if (!cancelled && im.naturalHeight) setFreeAspect(im.naturalWidth / im.naturalHeight); };
        im.src = displayImage;
        return () => { cancelled = true; };
    }, [isFreeSize, displayImage]);

    // CropStage produced the cropped file → drop it into the canvas.
    const handleCropApply = useCallback((croppedFile) => {
        setShowCropModal(false);
        setCropImageSrc(null);
        pendingCropFileRef.current = null;
        commitSourceFile(croppedFile, { fromHistory: false });
    }, [commitSourceFile]);

    // "Skip crop" → use the un-cropped (but resized) source as-is.
    const handleCropSkip = useCallback(() => {
        setShowCropModal(false);
        setCropImageSrc(null);
        const f = pendingCropFileRef.current;
        pendingCropFileRef.current = null;
        if (f) commitSourceFile(f, { fromHistory: false });
    }, [commitSourceFile]);

    // CropStage failed to load/draw → fall back to the original source.
    const handleCropError = useCallback(() => {
        notifications.show({ message: t('imageManualEditor.imageLoadFailed'), color: 'orange' });
        handleCropSkip();
    }, [handleCropSkip, t]);

    // Modal closed via the X / Esc → apply the current selection (matches the AI editor).
    const closeCropModal = useCallback(() => {
        cropStageRef.current?.apply();
    }, []);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDraggingOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && (file.type.startsWith('image/') || !file.type || file.type === 'application/octet-stream')) handleFileSelect(file);
    };

    // Paste-to-upload: copy an image anywhere and Ctrl/Cmd+V it into the upload
    // area. Only acts when the clipboard holds an image, so pasting text is safe.
    useEffect(() => {
        const onPaste = (e) => {
            const items = e.clipboardData?.items;
            if (!items) return;

            let imageFile = null;
            for (const item of items) {
                if (item.kind === 'file' && item.type.startsWith('image/')) { imageFile = item.getAsFile(); break; }
            }
            if (!imageFile) return; // no image on clipboard → allow default paste

            // Single upload area active → run through the shared validation path
            if (!isDualMode && !displayImage) {
                e.preventDefault();
                handleFileSelect(imageFile);
                return;
            }

            // Dual mode → fill the first empty zone
            if (isDualMode && (!mergeLeft || !mergeRight)) {
                if (isUnsupportedUpload(imageFile)) {
                    notifications.show({ message: t('imageManualEditor.avifNotSupported'), color: 'red' });
                    return;
                }
                e.preventDefault();
                const url = URL.createObjectURL(imageFile);
                if (!mergeLeft) setMergeLeft({ url, offsetX: 0, offsetY: 0, zoom: 1 });
                else setMergeRight({ url, offsetX: 0, offsetY: 0, zoom: 1 });
            }
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [isDualMode, displayImage, mergeLeft, mergeRight, handleFileSelect, t]);

    // Ctrl/Cmd+U opens the file picker while the single upload area is visible.
    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U') && !isDualMode && !displayImage) {
                e.preventDefault();
                fileInputRef.current?.click();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [isDualMode, displayImage]);

    // ── Remove Background (Web Worker — keeps main thread free) ──────────────
    const handleRemoveBg = async () => {
        // In dual mode: skip only if BOTH panels already have bg removed
        const dualBothDone = isDualMode && !!(mergeLeft?.bgRemoved && mergeRight?.bgRemoved);
        if (isRemovingBg || (!isDualMode && bgRemoved) || dualBothDone) return;
        if (!isDualMode && !previewUrl) return;
        if (isDualMode && (!mergeLeft || !mergeRight)) return;

        // ── Credit check (operators use owner's credit from usageStats) ──────
        const effectiveCredit = selectedOfficeSpace?.id
            ? (usageStats?.credit ?? user?.credit ?? 0)
            : (user?.credit ?? 0);
        if (effectiveCredit <= 0) {
            notifications.show({ message: t('imageManualEditor.noCreditBgRemove'), color: 'red' });
            return;
        }

        setIsRemovingBg(true);
        const setProgressText = (text) => {
            if (bgProgressRef.current)      bgProgressRef.current.textContent      = text;
            if (bgProgressLabelRef.current) bgProgressLabelRef.current.textContent = text;
        };
        setProgressText(t('imageManualEditor.processing'));

        // Yield to the browser so the loading overlay paints BEFORE worker module parsing blocks the thread
        await new Promise(r => requestAnimationFrame(r));

        // Helper: run bg-removal worker on a single URL, returns compressed Blob
        const runBgWorker = (imageUrl, labelPrefix) => new Promise((resolve, reject) => {
            const worker = new Worker(new URL('./bgRemovalWorker.js', import.meta.url), { type: 'module' });
            worker.onmessage = (e) => {
                const { type, blob, message } = e.data;
                if (type === 'progress') {
                    // Always show a single, steady label — no percentages or stage switches.
                    setProgressText(t('imageManualEditor.processing'));
                } else if (type === 'done') {
                    worker.terminate();
                    resolve(blob);
                } else if (type === 'error') {
                    worker.terminate();
                    reject(new Error(message));
                }
            };
            worker.onerror = (err) => { worker.terminate(); reject(err); };
            worker.postMessage({ imageUrl });
        });

        try {
            if (isDualMode) {
                // ── Dual mode: only process panels that haven't had bg removed yet ──
                // Figure out which panels need removal for accurate progress labels
                const needLeft  = !mergeLeft?.bgRemoved;
                const needRight = !mergeRight?.bgRemoved;
                const total     = (needLeft ? 1 : 0) + (needRight ? 1 : 0);
                let done = 0;

                if (needLeft) {
                    done++;
                    const label = total > 1 ? `(${done}/${total}) ${t('imageManualEditor.removing')}` : t('imageManualEditor.removing');
                    const leftBlob = await runBgWorker(mergeLeft.url, label);
                    const leftUrl  = URL.createObjectURL(leftBlob);
                    setMergeLeft(prev => prev ? { ...prev, url: leftUrl, bgRemoved: true } : prev);
                }

                if (needRight) {
                    done++;
                    const label = total > 1 ? `(${done}/${total}) ${t('imageManualEditor.removing')}` : t('imageManualEditor.removing');
                    const rightBlob = await runBgWorker(mergeRight.url, label);
                    const rightUrl  = URL.createObjectURL(rightBlob);
                    setMergeRight(prev => prev ? { ...prev, url: rightUrl, bgRemoved: true } : prev);
                }

                // Keep panels visible — do NOT set processedUrl or clear mergeLeft/Right
            } else {
                // ── Single mode: existing path ───────────────────────────────────
                const blob = await runBgWorker(previewUrl, t('imageManualEditor.removing'));
                setProcessedUrl(URL.createObjectURL(blob));
            }
            setBgRemoved(true);
            notifications.show({ message: t('imageManualEditor.bgRemovedSuccess'), color: 'green' });

            // Auto-save the freshly cut-out image (create record → update mode).
            // Only on first removal; later edits use the Update button.
            if (!currentEditId) setAutoSavePending(true);

            // ── Deduct credit (fire-and-forget, don't block the UI) ───────────
            imageGenerationService.deductManualBgCredit({
                officeSpaceId: selectedOfficeSpace?.id,
            })
                .then(() => {
                    // Refresh credit balance in background
                    getCurrentUser().catch(() => {});
                    if (selectedOfficeSpace?.id) {
                        usageService.getUserUsageStats(selectedOfficeSpace.id)
                            .then(stats => setUsageStats(stats))
                            .catch(() => {});
                    }
                })
                .catch(err => {
                    console.error('Credit deduction failed:', err);
                });
        } catch (err) {
            console.error('BG removal error:', err);
            notifications.show({ message: t('imageManualEditor.bgRemoveError'), color: 'red' });
        } finally {
            setIsRemovingBg(false);
        }
    };

    // ── Color click: auto-remove BG on first colour pick ─────────────────────
    const handleColorClick = (itemId) => {
        setBg(itemId);
        setBgImageSrc(''); // leaving any gradient/pattern/image background
        if (itemId === 'custom') {
            colorPickerRef.current?.click();
            // Don't trigger bg removal yet — wait for user to pick a color
            return;
        }
        const needsBgRemoval = isDualMode
            ? !(mergeLeft?.bgRemoved && mergeRight?.bgRemoved)
            : !bgRemoved;
        if (needsBgRemoval) handleRemoveBg();
    };

    // ── Preset image background pick (same presets as the advanced editor) ─────
    const handlePresetClick = (preset) => {
        setBg(`preset:${preset.id}`);
        setBgImageSrc(preset.src);
        const needsBgRemoval = isDualMode
            ? !(mergeLeft?.bgRemoved && mergeRight?.bgRemoved)
            : !bgRemoved;
        if (needsBgRemoval) handleRemoveBg();
    };

    // AI face enhance (GFPGAN) — runs in-browser and renders the result directly
    // in the main preview area (no modal). A loading overlay shows over the photo.
    const handleFaceEnhance = async (roi = null) => {
        if (!displayImage || isFaceEnhancing) return;
        setIsFaceEnhancing(true);
        try {
            const blob = await runFaceEnhance(displayImage, () => {}, roi);
            setProcessedUrl(URL.createObjectURL(blob));
            if (!currentEditId) setAutoSavePending(true);
            notifications.show({ message: t('faceEnhance.done'), color: 'green' });
        } catch (err) {
            console.error('[FaceEnhance] failed:', err);
            const msg = err?.message === 'NO_FACE' ? t('faceEnhance.noFace') : t('faceEnhance.error');
            notifications.show({ message: msg, color: 'red' });
        } finally {
            setIsFaceEnhancing(false);
        }
    };

    /**
     * One-click 4× super-resolution on the working photo, using the heavy
     * (Real-ESRGAN) model the upscale tool calls "detailed". No options: the
     * button picks the scale and the model, and the result replaces the photo
     * exactly the way face enhance does.
     *
     * Two automatic fallbacks, so the button never dead-ends: 4× that would blow
     * past the browser's pixel ceiling drops to 2×, and a machine whose WebGPU
     * cannot run Real-ESRGAN gets the light model instead of an error.
     */
    const handleUpscale = async () => {
        if (!displayImage || isUpscaling || isFaceEnhancing) return;
        setIsUpscaling(true);
        try {
            const res = await fetch(displayImage);
            const blob = await res.blob();
            const file = new File([blob], 'photo.png', { type: blob.type || 'image/png' });

            const { width, height } = await readImageDimensions(file);
            const scale = fitsOutputLimit(width, height, 4) ? 4 : 2;
            if (!fitsOutputLimit(width, height, scale)) {
                notifications.show({ message: t('upscaleImage.errors.outputTooLarge'), color: 'red' });
                return;
            }

            let out;
            try {
                out = await upscaleImageFile(file, { engine: 'aiMax', scale, format: 'png' }, () => {});
            } catch (err) {
                if (err?.code !== 'NO_WEBGPU') throw err;
                out = await upscaleImageFile(file, { engine: 'ai', scale, format: 'png' }, () => {});
            }

            setProcessedUrl(URL.createObjectURL(out.blob));
            if (!currentEditId) setAutoSavePending(true);
            notifications.show({ message: t('upscaleImage.done'), color: 'green' });
        } catch (err) {
            console.error('[Upscale] failed:', err);
            notifications.show({ message: t('upscaleImage.errors.failed'), color: 'red' });
        } finally {
            setIsUpscaling(false);
        }
    };

    // Dual mode: enhance each panel's photo independently (GFPGAN needs a single
    // face per image), so left and right are processed separately and one failing
    // (e.g. NO_FACE) doesn't block the other.
    const handleFaceEnhanceDual = async () => {
        if (isFaceEnhancing || (!mergeLeft && !mergeRight)) return;
        setIsFaceEnhancing(true);
        let anyOk = false, anyFail = false;
        try {
            for (const [panel, setPanel] of [[mergeLeft, setMergeLeft], [mergeRight, setMergeRight]]) {
                if (!panel?.url) continue;
                try {
                    const blob = await runFaceEnhance(panel.url, () => {});
                    const url = URL.createObjectURL(blob);
                    setPanel(prev => prev ? { ...prev, url } : prev);
                    anyOk = true;
                } catch (err) {
                    console.error('[FaceEnhance dual] failed:', err);
                    anyFail = true;
                }
            }
            if (anyOk) notifications.show({ message: t('faceEnhance.done'), color: 'green' });
            else if (anyFail) notifications.show({ message: t('faceEnhance.noFace'), color: 'red' });
        } finally {
            setIsFaceEnhancing(false);
        }
    };

    // Object tool result — a full-resolution PNG with the masked Face / Skin /
    // Hair adjustments baked in. Replaces the working image, same as face enhance.
    const handleObjectApply = (blob) => {
        setObjectPreviewUrl(null);
        setProcessedUrl(URL.createObjectURL(blob));
        if (!currentEditId) setAutoSavePending(true);
    };

    // ── Manual face-area selection ────────────────────────────────────────────
    // Enter selection mode with a centred box the user positions over the face.
    const enterFaceSelect = () => {
        const fr = frameRef.current;
        if (!fr || !displayImage) return;
        const fw = fr.clientWidth, fh = fr.clientHeight;
        const w = Math.round(fw * 0.5), h = Math.round(fh * 0.5);
        setFaceRect({ x: Math.round((fw - w) / 2), y: Math.round((fh - h) / 2), w, h });
        setFaceSelectMode(true);
    };
    const cancelFaceSelect = () => { setFaceSelectMode(false); setFaceRect(null); faceDragRef.current = null; };

    const startFaceDrag = (e, mode) => {
        e.preventDefault(); e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        faceDragRef.current = { mode, startX: e.clientX, startY: e.clientY, rect: { ...faceRect } };
    };
    const onFaceDrag = (e) => {
        const d = faceDragRef.current;
        const fr = frameRef.current;
        if (!d || !fr) return;
        const fw = fr.clientWidth, fh = fr.clientHeight, MIN = 40;
        const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
        let { x, y, w, h } = d.rect;
        if (d.mode === 'move') {
            x = Math.max(0, Math.min(fw - w, d.rect.x + dx));
            y = Math.max(0, Math.min(fh - h, d.rect.y + dy));
        } else {
            if (d.mode.includes('e')) w = Math.max(MIN, Math.min(fw - d.rect.x, d.rect.w + dx));
            if (d.mode.includes('s')) h = Math.max(MIN, Math.min(fh - d.rect.y, d.rect.h + dy));
            if (d.mode.includes('w')) { const nx = Math.min(d.rect.x + d.rect.w - MIN, Math.max(0, d.rect.x + dx)); w = d.rect.x + d.rect.w - nx; x = nx; }
            if (d.mode.includes('n')) { const ny = Math.min(d.rect.y + d.rect.h - MIN, Math.max(0, d.rect.y + dy)); h = d.rect.y + d.rect.h - ny; y = ny; }
        }
        setFaceRect({ x, y, w, h });
    };
    const endFaceDrag = () => { faceDragRef.current = null; };

    // Map the on-screen box → source-image pixel ROI, then enhance that region only.
    const confirmFaceSelect = () => {
        const fr = frameRef.current;
        const imgEl = fr?.querySelector('img');
        if (!fr || !imgEl?.naturalWidth || !faceRect) { cancelFaceSelect(); return; }
        const fw = fr.clientWidth, fh = fr.clientHeight;
        const iw = imgEl.naturalWidth, ih = imgEl.naturalHeight;
        const totalScale = Math.min(fw / iw, fh / ih) * zoom;
        const imgLeft = fw / 2 + offset.x - (iw * totalScale) / 2;
        const imgTop  = fh / 2 + offset.y - (ih * totalScale) / 2;
        let rx = Math.round((faceRect.x - imgLeft) / totalScale);
        let ry = Math.round((faceRect.y - imgTop) / totalScale);
        let rw = Math.round(faceRect.w / totalScale);
        let rh = Math.round(faceRect.h / totalScale);
        rx = Math.max(0, Math.min(iw - 1, rx));
        ry = Math.max(0, Math.min(ih - 1, ry));
        rw = Math.max(8, Math.min(iw - rx, rw));
        rh = Math.max(8, Math.min(ih - ry, rh));
        setFaceSelectMode(false);
        setFaceRect(null);
        handleFaceEnhance({ x: rx, y: ry, w: rw, h: rh });
    };

    // Open EraserModal — in dual mode, merge both images first so the modal shows the composite
    const handleOpenEraserModal = async () => {
        if (isDualMode && mergeLeft && mergeRight) {
            try {
                const blob = await doMergeDual('image/png', 1, true);
                if (dualEraserUrl) URL.revokeObjectURL(dualEraserUrl);
                const url = URL.createObjectURL(blob);
                setDualEraserUrl(url);
            } catch {
                // fall through — open modal with whatever is available
            }
        }
        setEraserModalOpen(true);
    };

    // ── Dual image helpers ────────────────────────────────────────────────────
    const handleMergePickSide = (side, e) => {
        const picked = e.target.files?.[0];
        e.target.value = '';
        if (!picked) return;
        if (isUnsupportedUpload(picked)) {
            notifications.show({ message: t('imageManualEditor.avifNotSupported'), color: 'red' });
            return;
        }
        // Allow files with no MIME type (e.g. from Google Photos on Android)
        if (picked.type && !picked.type.startsWith('image/') && picked.type !== 'application/octet-stream') return;
        const url = URL.createObjectURL(picked);
        if (side === 'left') setMergeLeft({ url, offsetX: 0, offsetY: 0, zoom: 1 });
        else setMergeRight({ url, offsetX: 0, offsetY: 0, zoom: 1 });
    };

    const handleZoomSide = (side, delta) => {
        const setter = side === 'left' ? setMergeLeft : setMergeRight;
        setter(prev => prev ? { ...prev, zoom: Math.max(1, Math.min(4, (prev.zoom ?? 1) + delta)) } : prev);
    };

    const handleZonePanStart = (e, side) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const cur = side === 'left' ? mergeLeft : mergeRight;
        if (!cur) return;
        dualDragRef.current = { side, startX: e.clientX, startY: e.clientY, startOX: cur.offsetX ?? 0, startOY: cur.offsetY ?? 0 };
    };

    const handleZonePanMove = (e, side) => {
        if (!dualDragRef.current || dualDragRef.current.side !== side) return;
        const d = dualDragRef.current;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        const zoneEl = (side === 'left' ? leftZoneRef : rightZoneRef).current;
        if (!zoneEl) return;
        const zW = zoneEl.offsetWidth;
        const zH = zoneEl.offsetHeight;
        const cur = side === 'left' ? mergeLeft : mergeRight;
        const zoom = cur?.zoom ?? 1;
        // Allow free panning up to half the zoomed image size in each direction
        const maxOX = zW  * zoom / 2;
        const maxOY = zH * zoom / 2;
        const newOX = Math.max(-maxOX, Math.min(maxOX, d.startOX + dx));
        const newOY = Math.max(-maxOY, Math.min(maxOY, d.startOY + dy));
        if (side === 'left') setMergeLeft(prev => ({ ...prev, offsetX: newOX, offsetY: newOY }));
        else setMergeRight(prev => ({ ...prev, offsetX: newOX, offsetY: newOY }));
    };

    const handleZonePanEnd = () => { dualDragRef.current = null; };

    const doMergeDual = (mimeType = 'image/jpeg', quality = 0.97, transparent = false) => new Promise((resolve, reject) => {
        if (!mergeLeft || !mergeRight) { reject(new Error('Both sides required')); return; }

        // Capture zone DOM dimensions SYNCHRONOUSLY here in the executor — before any async work.
        // If we read them inside .then(), React may have already re-rendered and removed
        // the zone divs from the DOM (leftZoneRef.current === null), causing wrong fallback values.
        const { exportW, exportH } = sizeConfig;
        const halfW = exportW / 2;
        const zLW = leftZoneRef.current?.offsetWidth  || halfW;
        const zLH = leftZoneRef.current?.offsetHeight || exportH;
        const zRW = rightZoneRef.current?.offsetWidth  || halfW;
        const zRH = rightZoneRef.current?.offsetHeight || exportH;

        const loadImg = (url) => new Promise((res, rej) => {
            const img = new window.Image(); img.onload = () => res(img); img.onerror = rej; img.src = url;
        });
        // Match the CSS preview exactly: objectFit=contain + zoom transform + offset
        const getContainDraw = (img, zW, zH, offsetX, offsetY, zoom = 1) => {
            const containScale = Math.min(zW / img.naturalWidth, zH / img.naturalHeight);
            const scale = containScale * zoom;
            const drawW = img.naturalWidth * scale;
            const drawH = img.naturalHeight * scale;
            const x = (zW - drawW) / 2 + (offsetX ?? 0);
            const y = (zH - drawH) / 2 + (offsetY ?? 0);
            return { x, y, drawW, drawH };
        };
        Promise.all([loadImg(mergeLeft.url), loadImg(mergeRight.url)]).then(([imgL, imgR]) => {
            const canvas = document.createElement('canvas');
            canvas.width = exportW; canvas.height = exportH;
            const ctx = canvas.getContext('2d');
            ctx.filter = `brightness(${brightness / 100}) contrast(${contrast / 100}) saturate(${saturation / 100})`;
            if (!transparent) {
                // Gradient/pattern paint synchronously; solid falls back to fill. (Preset/upload
                // image backgrounds aren't supported in dual mode and render as the solid fallback.)
                if (!paintSpecialBg(ctx, 0, 0, exportW, exportH, bg)) {
                    ctx.fillStyle = getBgColor();
                    ctx.fillRect(0, 0, exportW, exportH);
                }
            }

            // Left half — clipped to [0, halfW]
            const dL = getContainDraw(imgL, zLW, zLH, mergeLeft.offsetX, mergeLeft.offsetY, mergeLeft.zoom ?? 1);
            const xScaleL = halfW / zLW, yScaleL = exportH / zLH;
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, halfW, exportH);
            ctx.clip();
            ctx.drawImage(imgL, 0, 0, imgL.naturalWidth, imgL.naturalHeight,
                dL.x * xScaleL, dL.y * yScaleL, dL.drawW * xScaleL, dL.drawH * yScaleL);
            ctx.restore();

            // Right half — clipped to [halfW, exportW]
            const dR = getContainDraw(imgR, zRW, zRH, mergeRight.offsetX, mergeRight.offsetY, mergeRight.zoom ?? 1);
            const xScaleR = halfW / zRW, yScaleR = exportH / zRH;
            ctx.save();
            ctx.beginPath();
            ctx.rect(halfW, 0, halfW, exportH);
            ctx.clip();
            ctx.drawImage(imgR, 0, 0, imgR.naturalWidth, imgR.naturalHeight,
                halfW + dR.x * xScaleR, dR.y * yScaleR, dR.drawW * xScaleR, dR.drawH * yScaleR);
            ctx.restore();

            ctx.filter = 'none';
            if (sharp > 0) {
                const s = sharp / 10;
                const k = [0, -s, 0, -s, 1 + 4 * s, -s, 0, -s, 0];
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const { data, width: w, height: h } = imageData;
                const out = new Uint8ClampedArray(data.length);
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        let r = 0, g = 0, b = 0;
                        for (let ky = -1; ky <= 1; ky++) {
                            for (let kx = -1; kx <= 1; kx++) {
                                const px = clamp(x + kx, 0, w - 1);
                                const py = clamp(y + ky, 0, h - 1);
                                const i = (py * w + px) * 4;
                                const ki = (ky + 1) * 3 + (kx + 1);
                                r += data[i]     * k[ki];
                                g += data[i + 1] * k[ki];
                                b += data[i + 2] * k[ki];
                            }
                        }
                        const i = (y * w + x) * 4;
                        out[i]     = clamp(r, 0, 255);
                        out[i + 1] = clamp(g, 0, 255);
                        out[i + 2] = clamp(b, 0, 255);
                        out[i + 3] = data[i + 3];
                    }
                }
                ctx.putImageData(new ImageData(out, w, h), 0, 0);
            }
            // Honor the requested mime even for transparent output (PNG & WebP both
            // support alpha) so saves can be transparent WebP.
            canvas.toBlob(resolve, mimeType, quality);
        }).catch(reject);
    });

    // ── Reset all ─────────────────────────────────────────────────────────────
    const handleReset = () => {
        setSize('bd_passport');
        setBg('');
        setBgImageSrc('');
        setBrightness(100);
        setContrast(100);
        setSaturation(100);
        setSharp(0);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setRotation(0);
        setMergeLeft(null);
        setMergeRight(null);
        setBgRemoved(false);
        setProcessedUrl(null);
        setVisaCountryLabel('');
        setAutoEnhanced(false);
        setPreEnhanceVals(null);
    };

    // ── Render canvas → blob (exports only the visible/cropped area) ──────────
    const renderVisibleToBlob = async (exportW, exportH, mimeType = 'image/png', quality = 1, saveMode = false) => {
        const canvas = document.createElement('canvas');
        canvas.width = exportW; canvas.height = exportH;
        const ctx = canvas.getContext('2d');

        const img = new Image();
        img.crossOrigin = 'anonymous';
        const exportSrc = await getExportSourceUrl();
        try {
            await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = exportSrc.url; });
        } finally {
            if (exportSrc.revoke) setTimeout(() => URL.revokeObjectURL(exportSrc.url), 0);
        }

        // Apply rotation to a temp canvas so crop math works on rotated pixels
        let srcEl = img;
        let iw = img.naturalWidth, ih = img.naturalHeight;
        if (rotation !== 0) {
            const rad = (rotation * Math.PI) / 180;
            const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
            const rw = Math.round(iw * cos + ih * sin);
            const rh = Math.round(iw * sin + ih * cos);
            const rc = document.createElement('canvas');
            rc.width = rw; rc.height = rh;
            const rx = rc.getContext('2d');
            rx.translate(rw / 2, rh / 2);
            rx.rotate(rad);
            rx.drawImage(img, -iw / 2, -ih / 2);
            srcEl = rc; iw = rw; ih = rh;
        }

        const imgFilter = `brightness(${brightness / 100}) contrast(${contrast / 100}) saturate(${saturation / 100})`; // sharp applied via convolution below

        // Composite the background WITHOUT filter. On save (saveMode) the subject is kept
        // transparent so the bg is restored from the `background` token — EXCEPT uploaded
        // images, which have no token and so must be baked into the saved photo.
        const bakeBg = !saveMode || bg === 'image';
        if (bakeBg) await paintBgOntoCtx(ctx, exportW, exportH);

        ctx.filter = imgFilter;

        if (size === 'dual_image' || !frameRef.current) {
            if (size === 'dual_image') {
                const halfW = exportW / 2;
                const scale = Math.min(halfW / iw, exportH / ih);
                const dw = iw * scale, dh = ih * scale;
                ctx.drawImage(srcEl, (halfW - dw) / 2, (exportH - dh) / 2, dw, dh);
                ctx.drawImage(srcEl, halfW + (halfW - dw) / 2, (exportH - dh) / 2, dw, dh);
            } else {
                const scale = Math.min(exportW / iw, exportH / ih);
                const dw = iw * scale, dh = ih * scale;
                ctx.drawImage(srcEl, (exportW - dw) / 2, (exportH - dh) / 2, dw, dh);
            }
        } else {
            const frameW = frameRef.current.clientWidth;
            const frameH = frameRef.current.clientHeight;
            const containScale = Math.min(frameW / iw, frameH / ih);
            const totalScale   = containScale * zoom;
            const imgLeft = frameW / 2 + offset.x - (iw * totalScale) / 2;
            const imgTop  = frameH / 2 + offset.y - (ih * totalScale) / 2;
            const visLeft   = Math.max(0, imgLeft);
            const visTop    = Math.max(0, imgTop);
            const visRight  = Math.min(frameW, imgLeft + iw * totalScale);
            const visBottom = Math.min(frameH, imgTop + ih * totalScale);
            const sx = (visLeft  - imgLeft) / totalScale;
            const sy = (visTop   - imgTop)  / totalScale;
            const sw = (visRight - visLeft) / totalScale;
            const sh = (visBottom - visTop) / totalScale;
            const dx = (visLeft  / frameW) * exportW;
            const dy = (visTop   / frameH) * exportH;
            const dw = ((visRight - visLeft)  / frameW) * exportW;
            const dh = ((visBottom - visTop)  / frameH) * exportH;
            if (sw > 0 && sh > 0) ctx.drawImage(srcEl, sx, sy, sw, sh, dx, dy, dw, dh);
        }
        ctx.filter = 'none';

        // Apply sharpen convolution if needed
        if (sharp > 0) {
            const s = sharp / 10;
            const k = [0, -s, 0, -s, 1 + 4 * s, -s, 0, -s, 0];
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const { data, width: w, height: h } = imageData;
            const out = new Uint8ClampedArray(data.length);
            for (let y = 0; y < h; y++) {
                for (let x = 0; x < w; x++) {
                    let r = 0, g = 0, b = 0;
                    for (let ky = -1; ky <= 1; ky++) {
                        for (let kx = -1; kx <= 1; kx++) {
                            const px = clamp(x + kx, 0, w - 1);
                            const py = clamp(y + ky, 0, h - 1);
                            const i = (py * w + px) * 4;
                            const ki = (ky + 1) * 3 + (kx + 1);
                            r += data[i]     * k[ki];
                            g += data[i + 1] * k[ki];
                            b += data[i + 2] * k[ki];
                        }
                    }
                    const i = (y * w + x) * 4;
                    out[i]     = clamp(r, 0, 255);
                    out[i + 1] = clamp(g, 0, 255);
                    out[i + 2] = clamp(b, 0, 255);
                    out[i + 3] = data[i + 3];
                }
            }
            ctx.putImageData(new ImageData(out, w, h), 0, 0);
        }

        return new Promise((res) => canvas.toBlob(res, mimeType, quality));
    };

    // ── Helper: build FormData for saveManualEdit ──────────────────────────────
    // saveRendered=true means the blob already has crop/zoom baked in — skip position metadata
    const buildSaveFormData = async (aiBlob, { saveRendered = false } = {}) => {
        const fd = new FormData();
        fd.append('aiImage',  new File([aiBlob], `manual-edit-${size}.webp`, { type: 'image/webp' }));
        fd.append('size', size);
        // Background column: solid key, custom hex, or a special token (gradient:/pattern:/preset:).
        // Uploaded images ('image') are baked into the saved photo, so store 'transparent'.
        const backgroundField = bg === 'custom' ? customColor
            : bg === 'image' ? 'transparent'
            : (bg || 'white');
        fd.append('background', backgroundField);
        const effectiveBgRemoved = isDualMode
            ? !!(mergeLeft?.bgRemoved || mergeRight?.bgRemoved)
            : bgRemoved;
        fd.append('manualBgRemoved', effectiveBgRemoved ? 'true' : 'false');
        if (selectedOfficeSpace?.id) fd.append('officeSpaceId', selectedOfficeSpace.id);
        // Save view position only when storing the full uncropped transparent PNG.
        // When saveRendered=true the aiImage is already the final visible-area crop,
        // so zoom/offset metadata would cause double-zoom on re-edit.
        if (!saveRendered && bgRemoved && (zoom !== 1 || offset.x !== 0 || offset.y !== 0)) {
            const frameW = frameRef.current?.clientWidth || 1;
            const frameH = frameRef.current?.clientHeight || 1;
            const imgEl = frameRef.current?.querySelector('img');
            const nw = imgEl?.naturalWidth;
            const nh = imgEl?.naturalHeight;
            if (nw && nh) {
                const cs = Math.min(frameW / nw, frameH / nh);
                fd.append('zoom',    String(zoom));
                fd.append('offsetX', String(offset.x / (nw * cs)));
                fd.append('offsetY', String(offset.y / (nh * cs)));
            }
        }
        return fd;
    };

    // ── Composite transparent PNG onto a background-colored canvas ───────────
    const compositeOnBg = (url, w, h) => new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = getBgColor();
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            canvas.toBlob(b => b ? resolve(b) : reject(new Error('canvas toBlob failed')), 'image/jpeg', 0.97);
        };
        img.onerror = reject;
        img.src = url;
    });

    // ── Download ──────────────────────────────────────────────────────────────
    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
        if (isDualMode) {
            if (!mergeLeft || !mergeRight) return;
            let dualBlob;
            const dualHasTransparency = !!(mergeLeft?.bgRemoved || mergeRight?.bgRemoved);
            if (dualHasTransparency) {
                const { exportW, exportH } = sizeConfig;
                const transparentBlob = await doMergeDual('image/png', 1, true);
                const transparentUrl  = URL.createObjectURL(transparentBlob);
                dualBlob = await compositeOnBg(transparentUrl, exportW, exportH);
                URL.revokeObjectURL(transparentUrl);
            } else {
                dualBlob = await doMergeDual('image/jpeg', 0.97);
            }
            const url = URL.createObjectURL(dualBlob);
            const a = document.createElement('a');
            a.href = url; a.download = `photo-${size}.jpg`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
            return;
        }
        if (!previewUrl) return;
        const { W, H } = getExportDims();
        const blob = await renderVisibleToBlob(W, H, 'image/jpeg', 0.97);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `photo-${size}.jpg`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch (err) {
            console.error('Download failed:', err);
            notifications.show({ message: t('imageManualEditor.downloadError', 'Download failed'), color: 'red' });
        } finally {
            setIsDownloading(false);
        }
    };

    // ── Print (opens PrintModal) ──────────────────────────────────────────────
    const handleOpenPrint = async () => {
        if (isPrinting) return;
        setIsPrinting(true);
        try {
            let blob;
            if (isDualMode) {
                if (!mergeLeft || !mergeRight) return;
                const dualHasTransparency = !!(mergeLeft?.bgRemoved || mergeRight?.bgRemoved);
                if (dualHasTransparency) {
                    const { exportW, exportH } = sizeConfig;
                    const transparentBlob = await doMergeDual('image/png', 1, true);
                    const transparentUrl  = URL.createObjectURL(transparentBlob);
                    blob = await compositeOnBg(transparentUrl, exportW, exportH);
                    URL.revokeObjectURL(transparentUrl);
                } else {
                    blob = await doMergeDual('image/jpeg', 0.97);
                }
            } else {
                if (!previewUrl) return;
                const { W, H } = getExportDims();
                blob = await renderVisibleToBlob(W, H, 'image/jpeg', 0.97);
            }
            const url = URL.createObjectURL(blob);
            setPrintBlobUrl(url);
            setPrintIsFree(isFreeSize);
            // The rendered blob already has the background baked in — no id lookup needed.
            setPrintImageId(null);
            setShowPrintModal(true);
        } catch (err) {
            console.error('Print failed:', err);
            notifications.show({ message: t('imageManualEditor.printError', 'Print failed'), color: 'red' });
        } finally {
            setIsPrinting(false);
        }
    };

    const handleClosePrint = () => {
        setShowPrintModal(false);
        setPrintImageId(null);
        if (printBlobUrl) {
            setTimeout(() => { URL.revokeObjectURL(printBlobUrl); }, 1000);
            setPrintBlobUrl(null);
        }
    };

    // ── Save to history ───────────────────────────────────────────────────────
    const handleSave = async () => {
        const canSave = isDualMode ? (mergeLeft && mergeRight) : displayImage;
        if (!canSave || isSaving) return;
        // In update mode: allow re-save even if savedToHistory
        if (!currentEditId && savedToHistory) return;
        setIsSaving(true);
        try {
            let blob;
            if (isDualMode) {
                // Save transparent WebP (q70) — background colour is stored in 'background' field
                if (!mergeLeft || !mergeRight) return;
                blob = await doMergeDual('image/webp', 0.7, true);
            } else {
                // Save transparent WebP (q70) of the visible crop — bg colour stored separately
                const { W, H } = getExportDims();
                blob = await renderVisibleToBlob(W, H, 'image/webp', 0.7, true);
            }
            // saveRendered=true: visible-area crop is baked in, skip zoom/offset metadata
            const fd = await buildSaveFormData(blob, { saveRendered: true });
            const bgKey = bg === 'custom' ? customColor : (bg || 'white');

            let saved;
            if (currentEditId) {
                // Update existing record in place — preserves the same ID. The API
                // routes by source-tagged ref, so re-attach the manual tag.
                const res = await imageGenerationService.updateManualEdit(`m${currentEditId}`, fd);
                saved = res.data;
                setImageHistory(prev => [
                    { id: saved.id, aiImage: saved.aiImage, url: saved.aiImage, size: saved.size || size, background: saved.background || bgKey, manuallyEdited: true, manualBgRemoved: isDualMode ? !!(mergeLeft?.bgRemoved || mergeRight?.bgRemoved) : bgRemoved, zoom: saved.zoom ?? null, offsetX: saved.offsetX ?? null, offsetY: saved.offsetY ?? null },
                    ...prev.filter(img => manualIdNum(img.id) !== currentEditId),
                ]);
                // ID stays the same; URL already correct — no navigation needed
            } else {
                // Create new record — named from the user's naming settings
                // (সেটিংস → ছবির নাম), same pattern as the AI editor. Updates
                // keep the record's existing name (renames survive re-saves).
                const displayName = buildPhotoDisplayName({
                    shopName: (selectedOfficeSpace?.businessProfile?.shopName || selectedOfficeSpace?.name || user?.shopName || '').trim() || 'Sohozkaj',
                    settings: user?.settings?.photoNameSettings,
                    serial: nextManualSerial(),
                });
                fd.append('originalFilename', displayName);
                const res = await imageGenerationService.saveManualEdit(fd);
                saved = res.data;
                setSavedFileName(displayName);
                setImageHistory(prev => [{ id: saved.id, aiImage: saved.aiImage, url: saved.aiImage, size: saved.size || size, background: saved.background || bgKey, fileName: displayName, manuallyEdited: true, manualBgRemoved: isDualMode ? !!(mergeLeft?.bgRemoved || mergeRight?.bgRemoved) : bgRemoved, zoom: saved.zoom ?? null, offsetX: saved.offsetX ?? null, offsetY: saved.offsetY ?? null }, ...prev]);
                setCurrentEditId(manualIdNum(saved.id));
                skipBlockerRef.current = true;
                navigate(`?id=${saved.id}`, { replace: true });
                skipBlockerRef.current = false;
            }
            setSavedImageInfo({ id: saved.id, aiImage: saved.aiImage, size: saved.size || size, background: saved.background || bgKey, savedAt: saved.createdAt || saved.updatedAt || new Date().toISOString() });
            setSavedToHistory(true);
            notifications.show({ message: t('imageManualEditor.saveSuccess'), color: 'green' });
        } catch (err) {
            console.error('Save failed:', err);
            notifications.show({ message: t('imageManualEditor.bgRemoveError', 'Save failed'), color: 'red' });
        } finally {
            setIsSaving(false);
        }
    };

    // Auto-save once after background removal, then switch to update mode.
    // Deferred via a flag so the processed image / state has settled first.
    useEffect(() => {
        if (!autoSavePending) return;
        const ready = isDualMode ? (mergeLeft && mergeRight) : (displayImage && bgRemoved);
        if (!ready) return;
        // Already saved, or a save is in flight → nothing to auto-save.
        if (currentEditId || savedToHistory || isSaving) { setAutoSavePending(false); return; }
        setAutoSavePending(false);
        handleSave();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSavePending, displayImage, bgRemoved, mergeLeft, mergeRight, currentEditId, savedToHistory, isSaving]);

    // ── Pan & zoom ────────────────────────────────────────────────────────────

    /** Push the live values onto the image (and its two read-outs) with no render. */
    const paintView = useCallback(() => {
        const v = liveViewRef.current;
        const el = previewImgRef.current;
        if (el) {
            el.style.transform = `translate(${v.x}px,${v.y}px) scale(${v.zoom}) rotate(${v.rotation}deg)`;
        }
        if (zoomLabelRef.current) zoomLabelRef.current.textContent = `${Math.round(v.zoom * 100)}%`;
        if (rotationLabelRef.current) {
            rotationLabelRef.current.textContent = v.rotation > 0 ? `+${v.rotation}°` : `${v.rotation}°`;
        }
    }, []);

    // React owns these values between gestures; the refs mirror them so a drag
    // always starts from what is actually on screen, and the read-outs (which
    // React does not render) are repainted from the committed state.
    useEffect(() => {
        if (viewDraggingRef.current) return;
        liveViewRef.current = { zoom, rotation, x: offset.x, y: offset.y };
        if (zoomSliderRef.current) zoomSliderRef.current.value = String(Math.round(zoom * 100));
        if (rotationSliderRef.current) rotationSliderRef.current.value = String(rotation);
        paintView();
        // `displayImage` is in here because the toolbar and its read-outs only
        // mount once a photo exists — without it the first paint happens while
        // those refs are still null and the two labels start out blank.
    }, [zoom, rotation, offset.x, offset.y, displayImage, paintView]);

    /** Start a gesture: the easing that smooths button steps would lag a drag. */
    const beginViewDrag = useCallback(() => {
        viewDraggingRef.current = true;
        if (previewImgRef.current) previewImgRef.current.style.transition = 'none';
    }, []);

    /** End a gesture and hand the final values back to React, once. */
    const endViewDrag = useCallback(() => {
        if (!viewDraggingRef.current) return;
        viewDraggingRef.current = false;
        if (previewImgRef.current) previewImgRef.current.style.transition = PREVIEW_TRANSITION;
        const v = liveViewRef.current;
        setZoom(v.zoom);
        setRotation(v.rotation);
        setOffset({ x: v.x, y: v.y });
    }, []);

    /**
     * Client point → position within the photo, as a 0-1 fraction of each side.
     *
     * The preview carries `translate(...) scale(...) rotate(...)`, so the pointer
     * has to be walked back through that transform before it means anything in
     * image space; then through the object-fit: contain letterbox, since the
     * element box is usually taller or wider than the photo drawn inside it.
     * Returns null for a point outside the photo itself.
     */
    const clientToImagePoint = useCallback((clientX, clientY) => {
        const img = previewImgRef.current;
        const frame = frameRef.current;
        if (!img || !frame || !img.naturalWidth) return null;

        const rect = frame.getBoundingClientRect();
        let px = clientX - rect.left - rect.width / 2;
        let py = clientY - rect.top - rect.height / 2;

        const v = liveViewRef.current;
        px -= v.x;
        py -= v.y;
        const a = (-v.rotation * Math.PI) / 180;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const rx = (px * cos - py * sin) / (v.zoom || 1);
        const ry = (px * sin + py * cos) / (v.zoom || 1);

        // The contained photo inside the element box.
        const fit = Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
        const drawnW = img.naturalWidth * fit;
        const drawnH = img.naturalHeight * fit;
        const u = rx / drawnW + 0.5;
        const w = ry / drawnH + 0.5;
        if (u < 0 || u > 1 || w < 0 || w > 1) return null;
        return { x: u, y: w };
    }, []);

    const handleBrushDown = (e) => {
        if (!objectBrush) return;
        // The capture surface sits inside the photo frame, whose own pointer
        // handlers pan the view — without this the brush drags the photo out
        // from under itself and only the first dab ever lands.
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const p = clientToImagePoint(e.clientX, e.clientY);
        brushStrokeRef.current = p;
        if (!p) return;
        // Marks the undo point: one gesture, one entry in the panel's history.
        objectBrush.onStrokeStart?.();
        objectBrush.onStroke({ fromX: p.x, fromY: p.y, toX: p.x, toY: p.y });
    };
    /** On-screen diameter of the brush, so the ring matches what it will paint. */
    const brushScreenSize = useCallback(() => {
        const img = previewImgRef.current;
        if (!img || !img.naturalWidth || !objectBrush) return 0;
        const fit = Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
        const longEdge = Math.max(img.naturalWidth, img.naturalHeight) * fit;
        return objectBrush.size * longEdge * (liveViewRef.current.zoom || 1);
    }, [objectBrush]);

    /** Move the brush ring by hand — a setState per pointermove would re-render
     *  this whole editor, which is exactly the cost the view refs avoid. */
    const moveBrushCursor = useCallback((clientX, clientY) => {
        const ring = brushCursorRef.current;
        if (!ring) return;
        const d = brushScreenSize();
        ring.style.width = `${d}px`;
        ring.style.height = `${d}px`;
        ring.style.transform = `translate(${clientX - d / 2}px, ${clientY - d / 2}px)`;
        ring.style.opacity = d > 0 ? '1' : '0';
    }, [brushScreenSize]);

    /**
     * Loupe: a magnified circle of the photo under the cursor while a selection
     * brush is armed. Painting a jawline or the edge of a pair of glasses is
     * pixel work, and the photo on screen is fitted to a ~520px frame — without
     * this the user is aiming at detail too small to see.
     *
     * It reads from the preview <img>, which already has the mask tint baked in,
     * so the loupe shows the selection growing as it is painted.
     */
    const drawLoupe = useCallback((clientX, clientY, point) => {
        const cv = loupeRef.current;
        const img = previewImgRef.current;
        if (!cv || !img || !img.naturalWidth) return;

        if (!point) { cv.style.opacity = '0'; return; }

        const ctx = cv.getContext('2d');
        const size = LOUPE_SIZE;
        const dpr = window.devicePixelRatio || 1;
        if (cv.width !== size * dpr) { cv.width = size * dpr; cv.height = size * dpr; }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        // Screen pixels per natural pixel, so the loupe magnifies relative to
        // what is actually on screen rather than to the file's own resolution.
        const fit = Math.min(img.clientWidth / img.naturalWidth, img.clientHeight / img.naturalHeight);
        const perNat = fit * (liveViewRef.current.zoom || 1);
        const srcSpan = size / (perNat * LOUPE_ZOOM);

        const cx = point.x * img.naturalWidth;
        const cy = point.y * img.naturalHeight;

        ctx.clearRect(0, 0, size, size);
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.clip();
        ctx.fillStyle = '#0f1330';
        ctx.fillRect(0, 0, size, size);
        // Match the on-screen rotation, or the loupe would disagree with the photo.
        ctx.translate(size / 2, size / 2);
        ctx.rotate((liveViewRef.current.rotation * Math.PI) / 180);
        ctx.translate(-size / 2, -size / 2);
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(img, cx - srcSpan / 2, cy - srcSpan / 2, srcSpan, srcSpan, 0, 0, size, size);
        ctx.restore();

        // The brush, at the size it will actually paint.
        const brushD = brushScreenSize() * LOUPE_ZOOM;
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, Math.max(2, brushD / 2), 0, Math.PI * 2);
        ctx.strokeStyle = objectBrush?.mode === 'erase' ? '#f87171' : '#38bdf8';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, 1.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fill();

        // Sit above-left of the pointer, flipping near an edge so it never
        // covers the very spot being painted.
        const pad = 18;
        const flipX = clientX + pad + size > window.innerWidth;
        const flipY = clientY - pad - size < 0;
        const left = flipX ? clientX - pad - size : clientX + pad;
        const top = flipY ? clientY + pad : clientY - pad - size;
        cv.style.transform = `translate(${left}px, ${top}px)`;
        cv.style.opacity = '1';
    }, [brushScreenSize, objectBrush]);

    const handleBrushMove = (e) => {
        if (!objectBrush) return;
        e.stopPropagation();
        moveBrushCursor(e.clientX, e.clientY);
        const p = clientToImagePoint(e.clientX, e.clientY);
        drawLoupe(e.clientX, e.clientY, p);
        // Left button held? Otherwise this is just the cursor moving over the photo.
        if (!(e.buttons & 1)) { brushStrokeRef.current = null; return; }
        if (!p) { brushStrokeRef.current = null; return; }
        const prev = brushStrokeRef.current || p;
        objectBrush.onStroke({ fromX: prev.x, fromY: prev.y, toX: p.x, toY: p.y });
        brushStrokeRef.current = p;
    };
    const handleBrushUp = (e) => {
        e?.stopPropagation?.();
        brushStrokeRef.current = null;
    };

    const handlePointerDown = (e) => {
        if (!displayImage) return;
        // Painting the selection owns the drag; panning would fight it.
        if (objectBrush) return;
        // Don't capture pointer if the event originated from an interactive child element
        if (e.target !== e.currentTarget && e.target.closest('button, input, a, [role="button"], [role="slider"]')) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        dragRef.current = { x: e.clientX, y: e.clientY, ox: liveViewRef.current.x, oy: liveViewRef.current.y };
        beginViewDrag();
    };
    const handlePointerMove = (e) => {
        if (!dragRef.current) return;
        liveViewRef.current.x = dragRef.current.ox + e.clientX - dragRef.current.x;
        liveViewRef.current.y = dragRef.current.oy + e.clientY - dragRef.current.y;
        paintView();
    };
    const handlePointerUp = () => {
        if (!dragRef.current) return;
        dragRef.current = null;
        endViewDrag();
    };

    // Restore bg/bgRemoved metadata when loading a history image
    useEffect(() => {
        if (!previewUrl || !pendingHistoryMetaRef.current) return;
        const meta = pendingHistoryMetaRef.current;
        pendingHistoryMetaRef.current = null;
        // Mark as already transparent so user can freely change bg
        setProcessedUrl(previewUrl);
        setBgRemoved(meta.manualBgRemoved === true);
        if (meta.background) {
            const b = meta.background;
            if (isSpecialBgToken(b)) {
                // gradient:/pattern:/preset: token — restore directly (non-destructive)
                setBg(b);
                setBgImageSrc(parseBgToken(b).kind === 'preset' ? presetSrc(parseBgToken(b).id) : '');
            } else if (BG_HEX[b] !== undefined || b === 'transparent') {
                setBg(b);
                setBgImageSrc('');
            } else if (b.startsWith('#') || b.startsWith('rgb')) {
                setBg('custom');
                setCustomColor(b);
                setBgImageSrc('');
            }
        }
        if (meta.size && (meta.size === 'free' || PHOTO_SIZES_CONFIG[meta.size])) {
            setSize(meta.size);
        }
        // Restore saved view position — defer pixel conversion until image onLoad
        // (we need naturalWidth/Height to convert normalized offset → pixel offset)
        if (meta.zoom != null) {
            // Clamped so a saved zoom can never land outside what the toolbar
            // can express (and drag back from).
            setZoom(clamp(meta.zoom, ZOOM_MIN, ZOOM_MAX));
            setOffset({ x: 0, y: 0 });
            if (meta.offsetX != null || meta.offsetY != null) {
                pendingOffsetRestoreRef.current = { offsetX: meta.offsetX ?? 0, offsetY: meta.offsetY ?? 0 };
            } else {
                pendingOffsetRestoreRef.current = null;
            }
        } else {
            setZoom(1);
            setOffset({ x: 0, y: 0 });
            pendingOffsetRestoreRef.current = null;
        }
        // EraserModal will use the main editor zoom/offset (eraserStartFromSaved=false triggers coordinate conversion)
        setEraserZoom(null);
        setEraserOffset(null);
        setEraserStartFromSaved(false);
    }, [previewUrl]);

    // Mouse-wheel / cursor zoom on the single-image canvas was removed by request —
    // zoom is now driven only by the toolbar buttons and slider below the canvas.

    // ── Dual-mode mouse-wheel zoom ────────────────────────────────────────────
    useEffect(() => {
        const attach = (ref, setter) => {
            const el = ref.current;
            if (!el) return () => {};
            const handler = (e) => {
                e.preventDefault();
                setter(prev => prev ? { ...prev, zoom: Math.max(1, Math.min(4, (prev.zoom ?? 1) * (e.deltaY < 0 ? 1.1 : 0.9))) } : prev);
            };
            el.addEventListener('wheel', handler, { passive: false });
            return () => el.removeEventListener('wheel', handler);
        };
        const detachL = attach(leftZoneRef,  setMergeLeft);
        const detachR = attach(rightZoneRef, setMergeRight);
        return () => { detachL(); detachR(); };
    }, [mergeLeft, mergeRight]);

    // ── QR session ────────────────────────────────────────────────────────────
    const stopQrSession = useCallback(() => {
        clearInterval(qrTimerRef.current);
        clearInterval(qrPollRef.current);
    }, []);
    useEffect(() => () => stopQrSession(), [stopQrSession]);

    // ── Load saved manual-edit history from server ────────────────────────────
    const loadManualHistory = useCallback(async (page = 1, append = false) => {
        if (isLoadingHistory || (!hasMoreHistory && append)) return;
        if (!isAdmin && !selectedOfficeSpace) return;
        try {
            setIsLoadingHistory(true);
            const res = await imageGenerationService.getHistory({
                page,
                limit: 20,
                source: 'manual',
                ...(selectedOfficeSpace?.id ? { officeSpaceId: selectedOfficeSpace.id } : {}),
            });
            if (res.data?.history) {
                const items = res.data.history.map(h => ({ id: h.id, aiImage: h.aiImage, url: h.aiImage, size: h.size, photoSize: h.photoSize, background: h.background, manuallyEdited: true, manualBgRemoved: h.manualBgRemoved ?? false, zoom: h.zoom ?? null, offsetX: h.offsetX ?? null, offsetY: h.offsetY ?? null }));
                if (append) {
                    setImageHistory(prev => {
                        const ids = new Set(prev.map(x => x.id));
                        return [...prev, ...items.filter(x => !ids.has(x.id))];
                    });
                } else {
                    setImageHistory(items);
                }
                const pg = res.data.pagination;
                setHasMoreHistory(pg ? pg.page < pg.totalPages : items.length === 20);
                setHistoryPage(page);
            }
        } catch (err) {
            console.error('Failed to load manual history:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    }, [isAdmin, selectedOfficeSpace, isLoadingHistory, hasMoreHistory]);

    useEffect(() => { loadManualHistory(1); }, [selectedOfficeSpace]);

    // Load usage stats (needed for operator credit check)
    useEffect(() => {
        usageService.getUserUsageStats(selectedOfficeSpace?.id || undefined)
            .then(stats => setUsageStats(stats))
            .catch(() => {});
    }, [selectedOfficeSpace]);

    const openQrModal = async () => {
        if (qrStatus === 'loading') return;
        stopQrSession();
        if (qrToken) qrSessionService.cancelSession(qrToken).catch(() => {});
        setQrDataUrl(null); setQrToken(null); setQrMobileUrl(null);
        setQrTimeLeft(300); setQrStatus('loading'); setShowQrModal(true);
        try {
            const sessionMode = isDualMode ? 'dual' : 'single';
            const res = await qrSessionService.createSession(sessionMode);
            const { token, mobileUrl } = res.data;
            setQrToken(token); setQrMobileUrl(mobileUrl);
            const dataUrl = await QRCode.toDataURL(mobileUrl, { width: 220, margin: 2 });
            setQrDataUrl(dataUrl); setQrStatus('ready');
            const expiresAt = Date.now() + 5 * 60 * 1000;
            qrTimerRef.current = setInterval(() => {
                const left = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
                setQrTimeLeft(left);
                if (left === 0) { setQrStatus('expired'); stopQrSession(); }
            }, 1000);
            qrPollRef.current = setInterval(async () => {
                try {
                    const statusRes = await qrSessionService.getStatus(token);
                    const data = statusRes.data;
                    if (data.status === 'received') {
                        stopQrSession(); setQrStatus('received'); setShowQrModal(false);
                        const b64ToUrl = (b64, mime) => {
                            const bs = atob(b64); const ab = new ArrayBuffer(bs.length); const ia = new Uint8Array(ab);
                            for (let i = 0; i < bs.length; i++) ia[i] = bs.charCodeAt(i);
                            return URL.createObjectURL(new Blob([ab], { type: mime }));
                        };
                        if (data.mode === 'dual') {
                            setMergeLeft({ url: b64ToUrl(data.leftImageData, data.leftMimeType || 'image/jpeg'), offsetX: 0, offsetY: 0, zoom: 1 });
                            setMergeRight({ url: b64ToUrl(data.rightImageData, data.rightMimeType || 'image/jpeg'), offsetX: 0, offsetY: 0, zoom: 1 });
                        } else if (isDualMode) {
                            const url = b64ToUrl(data.imageData, data.imageMimeType || 'image/jpeg');
                            if (!mergeLeft) setMergeLeft({ url, offsetX: 0, offsetY: 0, zoom: 1 });
                            else setMergeRight({ url, offsetX: 0, offsetY: 0, zoom: 1 });
                        } else {
                            const mime = data.imageMimeType || 'image/jpeg';
                            const bs = atob(data.imageData); const ab = new ArrayBuffer(bs.length); const ia = new Uint8Array(ab);
                            for (let i = 0; i < bs.length; i++) ia[i] = bs.charCodeAt(i);
                            handleFileSelect(new File([new Blob([ab], { type: mime })], 'mobile-upload.jpg', { type: mime }));
                        }
                        notifications.show({ message: t('imageGeneration.qrReceivedMessage'), color: 'green' });
                    } else if (data.status === 'expired' && Date.now() >= expiresAt) {
                        stopQrSession(); setQrStatus('expired');
                    }
                } catch {}
            }, 2500);
        } catch {
            setQrStatus('idle'); setShowQrModal(false);
            notifications.show({ message: 'Could not create QR session', color: 'red' });
        }
    };

    const closeQrModal = () => {
        if (qrToken) qrSessionService.cancelSession(qrToken).catch(() => {});
        stopQrSession(); setShowQrModal(false); setQrStatus('idle');
    };

    const formatQrTime = (s) =>
        `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

    // ── Data ──────────────────────────────────────────────────────────────────
    const photoSizes = [
        { value: 'bd_passport',  abbr: t('imageGeneration.abbrPassport'),  icon: IconUserSquare,  tooltip: t('imageGeneration.bdPassport')  },
        { value: 'dual_image',   abbr: t('imageGeneration.abbrDual'),      icon: IconFriends,     tooltip: t('imageGeneration.dualImage')    },
        { value: 'bd_visa',      abbr: t('imageGeneration.abbrVisa'),      icon: IconPlaneTilt,   tooltip: t('imageGeneration.bdVisa')       },
    ];

    // Print size selection handler.
    const applyPrintSize = (key) => {
        // Leaving dual mode → restore the single image from the left slot.
        if (size === 'dual_image') {
            if (mergeLeft) setPreviewUrl(mergeLeft.url);
            setMergeLeft(null); setMergeRight(null);
            setProcessedUrl(null); setBgRemoved(false);
        }
        setAutoEnhanced(false); setPreEnhanceVals(null);
        setSize(key); setVisaCountryLabel('');
    };

    const backgrounds = [
        { id: 'white',       color: '#ffffff',  label: t('imageGeneration.bgWhite')       },
        { id: 'blue',        color: '#2196F3',  label: t('imageGeneration.bgBlue')        },
        { id: 'sky_blue',    color: '#03A9F4',  label: t('imageGeneration.bgSkyBlue')     },
        { id: 'grey',        color: '#9E9E9E',  label: t('imageGeneration.bgGrey')        },
        { id: 'dark_blue',   color: '#3068E1',  label: t('imageGeneration.bgDarkBlue')    },
        { id: 'teal',        color: '#00897B',  label: t('imageGeneration.bgTeal')        },
        { id: 'off_white',   color: '#FFF8DC',  label: t('imageGeneration.bgOffWhite')    },
        // 'transparent' moved to the AI Tools section as a dedicated tool card.
        { id: 'custom',      color: null,       label: t('imageGeneration.bgCustom')      },
    ];

    const imageLoaded = isDualMode ? (!!mergeLeft || !!mergeRight) : (!!previewUrl || !!processedUrl);
    // Remove the background on its own, without picking a colour first (same removal, same credit)
    const bgRemoveReady = isDualMode ? !!(mergeLeft && mergeRight) : !!previewUrl;
    const bgRemoveDone = isDualMode ? !!(mergeLeft?.bgRemoved && mergeRight?.bgRemoved) : bgRemoved;
    const removeBgButton = bgRemoveReady && (
        <button
            type="button"
            onClick={() => { setActiveDrawer(null); handleRemoveBg(); }}
            disabled={bgRemoveDone || isRemovingBg || isFaceEnhancing || isUpscaling}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-colors ${bgRemoveDone ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400' : 'bg-primary hover:bg-primary/90 text-white disabled:opacity-50'}`}
        >
            {bgRemoveDone ? <IconCheck size={16} /> : <IconEraser size={16} />}
            {t(isRemovingBg ? 'imageManualEditor.removing' : bgRemoveDone ? 'imageManualEditor.bgRemoved' : 'imageManualEditor.removeBackground')}
        </button>
    );

    const hasChanges = !savedToHistory && imageLoaded && (size !== 'bd_passport' || bg !== '' || bgRemoved || brightness !== 100 || contrast !== 100 || saturation !== 100 || rotation !== 0);
    const hasAdjustmentsChanged = brightness !== 100 || contrast !== 100 || saturation !== 100 || sharp !== 0;

    // ── Navigation guard (unsaved changes) ────────────────────────────────────
    const { navigator } = useContext(NavigationContext);

    // beforeunload: browser tab close / refresh
    useEffect(() => {
        const handler = (e) => { if (hasChanges) { e.preventDefault(); e.returnValue = ''; } };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasChanges]);

    // Intercept React Router in-app navigation (push / replace)
    useEffect(() => {
        if (!hasChanges) return;
        const originalPush    = navigator.push.bind(navigator);
        const originalReplace = navigator.replace.bind(navigator);

        navigator.push = (...args) => {
            if (skipBlockerRef.current) { originalPush(...args); return; }
            pendingNavRef.current = () => originalPush(...args);
            setShowDiscardModal(true);
        };
        navigator.replace = (...args) => {
            if (skipBlockerRef.current) { originalReplace(...args); return; }
            pendingNavRef.current = () => originalReplace(...args);
            setShowDiscardModal(true);
        };

        return () => {
            navigator.push    = originalPush;
            navigator.replace = originalReplace;
        };
    }, [hasChanges, navigator]);



    // ── The right action column (the AI photo editor's) ─────────────────────
    const compactActions = useMediaQuery('(max-width: 1279px)'); // icon strip, names as tooltips
    // with a photo in — or below 1024px, where the column is a QR strip until then (the old bottom bar is gone)
    const actionColumn = !isMobile && (imageLoaded || isCompact);
    const exportReady = isDualMode ? !!(mergeLeft && mergeRight) : !!previewUrl;
    const handleCopy = async () => {
        try {
            let blob;
            if (isDualMode) blob = doMergeDual('image/png', 1, false);
            else { const { W, H } = getExportDims(); blob = renderVisibleToBlob(W, H, 'image/png', 1); }
            await copyImageToClipboard(blob);
            notifications.show({ title: t('imageGeneration.imageCopied'), message: t('imageGeneration.imageCopiedPasteHint'), color: 'green', autoClose: 7000 });
        } catch {
            notifications.show({ title: t('imageGeneration.notifications.shareFailed'), message: t('imageGeneration.notifications.shareFailedMessage'), color: 'red' });
        }
    };
    // A saved row can come without its name (e.g. opened from another page): fetch it when the info popover opens.
    const loadSavedName = async (open) => {
        if (!open || !savedImageInfo?.id || savedFileName) return;
        const id = savedImageInfo.id;
        try {
            const rec = (await imageGenerationService.getImageById(id))?.data;
            const name = rec?.fileName || rec?.metadata?.originalFilename || imageHistory.find((h) => h.id === id)?.fileName || '';
            if (name && savedImageInfo?.id === id) setSavedFileName(name);
        } catch { /* the row stays "—" */ }
    };
    // readable size / background names instead of the stored keys (bd_passport, sky_blue, …)
    const sizeName = (key) => {
        const base = String(key).replace(/_l$/, '');
        const photo = photoSizes.find((x) => x.value === key);
        if (photo) return photo.tooltip;
        const print = PRINT_SIZES.find((x) => x.base === base);
        if (print) return `${print.label} (${print.sub})`;
        const c = sizeConfigs[key];
        return c ? `${c.wmm} × ${c.hmm} mm` : key;
    };
    const bgName = (key) => backgrounds.find((x) => x.id === key)?.label || (String(key).startsWith('#') ? key : key === 'transparent' ? t('imageGeneration.bgTransparent') : key);
    const infoRow = (label, value) => (
        <div className="flex items-start justify-between gap-3">
            <span className="shrink-0 text-zinc-500 dark:text-zinc-400">{label}:</span>
            <span className="min-w-0 break-words text-right font-medium text-zinc-800 dark:text-zinc-100">{value || '—'}</span>
        </div>
    );
    // the saved photo's details — the file name renames on double-click, like the AI photo editor
    const savedInfoBody = savedImageInfo && (
        <div className="flex flex-col gap-1.5 text-sm">
            {infoRow(t('imageManualEditor.fileNameLabel', 'ফাইলের নাম'), nameEditing ? (
                <input
                    autoFocus
                    value={nameInputVal}
                    maxLength={RENAME_MAX}
                    onChange={(e) => setNameInputVal(e.target.value)}
                    onBlur={handleSaveFileName}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveFileName(); if (e.key === 'Escape') setNameEditing(false); }}
                    className="w-48 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
                />
            ) : (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            className="inline-flex cursor-pointer items-center gap-1 border-b border-dashed border-current"
                            onDoubleClick={() => { setNameInputVal(savedFileName); setNameEditing(true); }}
                        >
                            {savedFileName || '—'}
                            <IconEdit size={13} className="shrink-0 opacity-60" onClick={() => { setNameInputVal(savedFileName); setNameEditing(true); }} />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="z-[100001]">{t('imageManualEditor.renameHint', 'ডাবল ক্লিক করে নাম বদলান')}</TooltipContent>
                </Tooltip>
            ))}
            {savedImageInfo.size && infoRow(t('imageManualEditor.sizeLabel', 'সাইজ'), sizeName(savedImageInfo.size))}
            {savedImageInfo.background && infoRow(t('imageManualEditor.bgLabel', 'ব্যাকগ্রাউন্ড'), bgName(savedImageInfo.background))}
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    const effectiveCreditForBanner = selectedOfficeSpace?.id
        ? (usageStats?.credit ?? user?.credit ?? 0)
        : (user?.credit ?? 0);

    return (
        <>
            {/* Full-page interaction blocker during bg removal */}
            {isRemovingBg && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, cursor: 'wait' }} />
            )}

            {user?.role === 'OWNER' && (
                <LowCreditBanner credit={effectiveCreditForBanner} />
            )}
            <style>{`
                @keyframes hourglass-flip {
                    0%,40%{transform:rotate(0deg)} 50%,90%{transform:rotate(180deg)} 100%{transform:rotate(360deg)}
                }
                .qr-hourglass{display:inline-flex;animation:hourglass-flip 3s ease-in-out infinite}
            `}</style>

            {/* Hidden SVG sharpen filter */}
            <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
                <defs>
                    <filter id="gim-sharpen">
                        <feConvolveMatrix order="3" preserveAlpha="true"
                            kernelMatrix={`0 ${-(sharp/10).toFixed(3)} 0 ${-(sharp/10).toFixed(3)} ${(1+4*(sharp/10)).toFixed(3)} ${-(sharp/10).toFixed(3)} 0 ${-(sharp/10).toFixed(3)} 0`}
                        />
                    </filter>
                </defs>
            </svg>

            <div className={`${styles.container} ${isMobile ? styles.containerMobile : ''} ${actionColumn ? styles.containerActions : ''}`}>

                {/* ══ LEFT SIDEBAR (order:1) ══ */}
                <div className={styles.leftSidebar} style={{ opacity: isRemovingBg ? 0.45 : 1, pointerEvents: isRemovingBg ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                    <div className={`flex flex-col flex-1 ${isMobile ? 'gap-2' : 'gap-5'}`}>

                        {/* Photo Size */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <div className={styles.sectionTitle}>{t('imageGeneration.photoSize')}</div>
                                {/* always reserve space; visible only when changes exist */}
                                <button
                                    title={t('imageGeneration.resetChanges')}
                                    onClick={handleReset}
                                    style={{ visibility: hasChanges ? 'visible' : 'hidden' }}
                                    className="w-7 h-7 flex items-center justify-center rounded-md text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                                >
                                    <IconRepeat size={14} />
                                </button>
                            </div>
                            <div className={styles.sizeGrid}>
                                {photoSizes.map((item) => {
                                    const isVisa = item.value === 'bd_visa';
                                    const isSelected = isVisa
                                        ? (size === 'bd_visa' || size.startsWith('visa_'))
                                        : size === item.value;

                                    const card = (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden', width: '100%' }}>
                                            <div
                                                className={`${styles.sizeCard} ${isSelected ? styles.selected : ''}`}
                                                onClick={() => {
                                                    if (isVisa) setVisaPopoverOpen(o => !o);
                                                    else {
                                                        const newSize = item.value;
                                                        if (newSize === 'dual_image' && previewUrl && size !== 'dual_image') {
                                                            // carry current single image to left slot
                                                            setMergeLeft({ url: processedUrl || previewUrl, offsetX: 0, offsetY: 0, zoom: 1 });
                                                            setPreviewUrl(null);
                                                        } else if (newSize !== 'dual_image' && size === 'dual_image') {
                                                            // leaving dual mode: restore image from left slot if available
                                                            if (mergeLeft) setPreviewUrl(mergeLeft.url);
                                                            setMergeLeft(null);
                                                            setMergeRight(null);
                                                        }
                                                        // Only reset processed state when entering/leaving dual mode
                                                        if (newSize === 'dual_image' || size === 'dual_image') {
                                                            setProcessedUrl(null);
                                                            setBgRemoved(false);
                                                        }
                                                        setAutoEnhanced(false);
                                                        setPreEnhanceVals(null);
                                                        setSize(newSize);
                                                        setVisaCountryLabel('');
                                                    }
                                                }}
                                            >
                                                <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <item.icon size={26} strokeWidth={1.5} />
                                                </div>
                                                {isSelected && (
                                                    <div className={styles.dressCheckmark}>
                                                        <IconCheck size={10} color="white" strokeWidth={3} />
                                                    </div>
                                                )}
                                            </div>
                                            <span className={`${styles.sizeCardLabel} ${isSelected ? styles.sizeCardLabelActive : ''}`}>
                                                {isVisa && visaCountryLabel ? visaCountryLabel : item.abbr}
                                            </span>
                                        </div>
                                    );

                                    if (isVisa) {
                                        return (
                                            <Tooltip key={item.value}>
                                                <Popover open={visaPopoverOpen} onOpenChange={setVisaPopoverOpen}>
                                                    <TooltipTrigger asChild>
                                                        <PopoverTrigger asChild>
                                                            <div style={{ width: '100%' }}>{card}</div>
                                                        </PopoverTrigger>
                                                    </TooltipTrigger>
                                                    <PopoverContent side="right" className="w-auto p-3 dark:bg-zinc-900" style={{ border: '2px solid #fe9a00', borderRadius: 12 }}>
                                                        <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 mb-2">
                                                            {isBn ? 'ভিসা ফটো মাপ' : t('imageManualEditor.visaPhotoSize')}
                                                        </p>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                                                            {VISA_GROUPS.map((group) => (
                                                                <button
                                                                    key={group.key}
                                                                    onClick={() => { setSize(group.key); setVisaCountryLabel(isBn ? group.sizeBn : group.size); setVisaPopoverOpen(false); }}
                                                                    className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all ${
                                                                        size === group.key
                                                                            ? 'bg-orange-500 text-white'
                                                                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                                                                    }`}
                                                                >
                                                                    {isBn ? group.sizeBn : group.size}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                                <TooltipContent side="bottom" className="z-[2000]">{item.tooltip}</TooltipContent>
                                            </Tooltip>
                                        );
                                    }
                                    return (
                                        <Tooltip key={item.value}>
                                            <TooltipTrigger asChild>
                                                <div style={{ width: '100%' }}>{card}</div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{item.tooltip}</TooltipContent>
                                        </Tooltip>
                                    );
                                })}

                                {/* Print / paper sizes (2R, 3R, …, A4, A4 landscape, Letter) grouped
                                    into one popover card. */}
                                {(() => {
                                    const selectedPrint = PRINT_SIZES.find((p) => p.base === size);
                                    const isSel = !!selectedPrint;
                                    const card = (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden', width: '100%' }}>
                                            <div
                                                className={`${styles.sizeCard} ${isSel ? styles.selected : ''}`}
                                                onClick={() => setPrintPopoverOpen((o) => !o)}
                                            >
                                                <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <IconPrinter size={26} strokeWidth={1.5} />
                                                </div>
                                                {isSel && (
                                                    <div className={styles.dressCheckmark}>
                                                        <IconCheck size={10} color="white" strokeWidth={3} />
                                                    </div>
                                                )}
                                            </div>
                                            <span className={`${styles.sizeCardLabel} ${isSel ? styles.sizeCardLabelActive : ''}`}>
                                                {selectedPrint ? selectedPrint.label : t('imageManualEditor.printSizeGroup')}
                                            </span>
                                        </div>
                                    );
                                    return (
                                        <Tooltip>
                                            <Popover open={printPopoverOpen} onOpenChange={setPrintPopoverOpen}>
                                                <TooltipTrigger asChild>
                                                    <PopoverTrigger asChild>
                                                        <div style={{ width: '100%' }}>{card}</div>
                                                    </PopoverTrigger>
                                                </TooltipTrigger>
                                                <PopoverContent side="right" className="w-auto p-3 dark:bg-zinc-900" style={{ border: '2px solid #fe9a00', borderRadius: 12 }}>
                                                    <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 mb-2">{t('imageManualEditor.printSizeTitle')}</p>
                                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                                                        {PRINT_SIZES.map((p) => (
                                                            <button
                                                                key={p.base}
                                                                onClick={() => { applyPrintSize(p.base); setPrintPopoverOpen(false); }}
                                                                className={`px-2 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                                                                    size === p.base
                                                                        ? 'bg-orange-500 text-white'
                                                                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                                                                }`}
                                                            >
                                                                {p.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </PopoverContent>
                                            </Popover>
                                            <TooltipContent side="bottom" className="z-[2000]">{t('imageManualEditor.printSizeTitle')}</TooltipContent>
                                        </Tooltip>
                                    );
                                })()}

                                {/* Free size — standalone card in the old A4 slot. Skips the crop
                                    and keeps the uploaded image's own aspect ratio. */}
                                {(() => {
                                    const isSel = size === 'free';
                                    return (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div style={{ width: '100%' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 0, overflow: 'hidden', width: '100%' }}>
                                                        <div className={`${styles.sizeCard} ${isSel ? styles.selected : ''}`} onClick={() => applyPrintSize('free')}>
                                                            <div style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                <IconArrowsMaximize size={24} strokeWidth={1.5} />
                                                            </div>
                                                            {isSel && (
                                                                <div className={styles.dressCheckmark}>
                                                                    <IconCheck size={10} color="white" strokeWidth={3} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <span className={`${styles.sizeCardLabel} ${isSel ? styles.sizeCardLabelActive : ''}`}>{t('imageManualEditor.freeSize')}</span>
                                                    </div>
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent side="bottom" className="z-[2000]">{t('imageManualEditor.freeSizeHint')}</TooltipContent>
                                        </Tooltip>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Background */}
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                <div className={styles.sectionTitle}>{t('imageGeneration.background')}</div>
                            </div>
                            <div className={styles.colorGrid} style={{ opacity: (!previewUrl && !isDualMode) || (isDualMode && !mergeLeft && !mergeRight) ? 0.35 : 1, pointerEvents: (!previewUrl && !isDualMode) || (isDualMode && !mergeLeft && !mergeRight) || isRemovingBg ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                                {backgrounds.map((item) => (
                                    <Tooltip key={item.id}>
                                        <TooltipTrigger asChild>
                                    <div
                                        className={`${styles.bgColorCard} ${bg === item.id ? styles.selected : ''}`}
                                        onClick={() => handleColorClick(item.id)}
                                        style={{
                                            background: item.id === 'custom'
                                                ? (bg === 'custom' ? customColor : 'linear-gradient(135deg,#667eea,#764ba2)')
                                                : item.id === 'transparent'
                                                    ? 'repeating-conic-gradient(#aaa 0% 25%, #fff 0% 50%) 0 0 / 14px 14px'
                                                    : item.color,
                                            cursor: 'pointer',
                                            position: 'relative',
                                        }}
                                    >
                                        {item.id === 'custom' ? (
                                            <>
                                                <IconPalette size={28} color="white" style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }} />
                                                <input
                                                    ref={colorPickerRef}
                                                    type="color"
                                                    value={customColor}
                                                    onChange={(e) => {
                                                        setCustomColor(e.target.value);
                                                        const needsBgRemoval = isDualMode
                                                            ? !(mergeLeft?.bgRemoved && mergeRight?.bgRemoved)
                                                            : !bgRemoved;
                                                        if (needsBgRemoval) handleRemoveBg();
                                                    }}
                                                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                                                />
                                            </>
                                        ) : (
                                            bg === item.id && (
                                                <div className={styles.dressCheckmark}>
                                                    <IconCheck size={12} color="white" strokeWidth={3} />
                                                </div>
                                            )
                                        )}
                                    </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">{item.label}</TooltipContent>
                                    </Tooltip>
                                ))}

                                {/* Preset image background picker (+) */}
                                <Tooltip>
                                    <Popover open={presetBgOpen} onOpenChange={setPresetBgOpen}>
                                        <TooltipTrigger asChild>
                                            <PopoverTrigger asChild>
                                                <div
                                                    className={`${styles.bgColorCard} ${parseBgToken(bg).kind === 'preset' ? styles.selected : ''}`}
                                                    style={{
                                                        background: parseBgToken(bg).kind === 'preset'
                                                            ? `center/cover no-repeat url(${bgImageSrc || presetSrc(parseBgToken(bg).id)})`
                                                            : 'var(--mantine-color-default)',
                                                        cursor: 'pointer',
                                                        position: 'relative',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}
                                                >
                                                    {parseBgToken(bg).kind === 'preset'
                                                        ? <div className={styles.dressCheckmark}><IconCheck size={12} color="white" strokeWidth={3} /></div>
                                                        : <IconPlus size={22} className="text-zinc-500 dark:text-zinc-300" />}
                                                </div>
                                            </PopoverTrigger>
                                        </TooltipTrigger>
                                        <PopoverContent side="top" align="end" className="w-auto p-3 dark:bg-zinc-900" style={{ border: '2px solid #fe9a00', borderRadius: 12 }}>
                                            <p className="text-xs font-semibold text-zinc-700 dark:text-zinc-200 mb-2">{isBn ? 'ছবি ব্যাকগ্রাউন্ড' : 'Image background'}</p>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, maxWidth: 230 }}>
                                                {BG_PRESETS.map((preset) => {
                                                    const sel = parseBgToken(bg).kind === 'preset' && parseBgToken(bg).id === preset.id;
                                                    return (
                                                        <button
                                                            key={preset.id}
                                                            title={preset.id}
                                                            onClick={() => { handlePresetClick(preset); setPresetBgOpen(false); }}
                                                            style={{
                                                                width: 38, height: 38, borderRadius: 8, overflow: 'hidden',
                                                                border: sel ? '2px solid #fe9a00' : '2px solid transparent',
                                                                background: `center/cover no-repeat url(${preset.src})`,
                                                                position: 'relative', cursor: 'pointer',
                                                            }}
                                                        >
                                                            {sel && <IconCheck size={13} color="#fff" strokeWidth={3} style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                    <TooltipContent side="bottom">{isBn ? 'ছবি ব্যাকগ্রাউন্ড' : 'Image background'}</TooltipContent>
                                </Tooltip>
                            </div>
                        </div>

                        {/* AI Tools — face enhancement, sits right below the background options.
                            Always shown (like the size/background sections); disabled until an image is loaded.
                            Rendered as an icon+label tool card, matching the AI photo-edit page. */}
                        <div style={{ marginTop: 4 }}>
                            <span className={styles.sectionTitle}>{t('imageManualEditor.aiTools')}</span>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 6, opacity: (isDualMode ? (mergeLeft || mergeRight) : displayImage) ? 1 : 0.35, pointerEvents: ((isDualMode ? (mergeLeft || mergeRight) : displayImage) && !isRemovingBg && !isFaceEnhancing && !isUpscaling) ? 'auto' : 'none', transition: 'opacity 0.2s' }}>
                                {[
                                    {
                                        key: 'faceEnhance',
                                        label: t('faceEnhance.button'),
                                        active: false,
                                        busy: isFaceEnhancing,
                                        onClick: () => { if (isFaceEnhancing) return; if (isDualMode) handleFaceEnhanceDual(); else if (displayImage) handleFaceEnhance(); },
                                        icon: <img src="/images/photo-tools/new-beutify-face.svg" alt="" width={40} className={styles.toolIcon} />,
                                    },
                                    {
                                        key: 'object',
                                        label: t('objectAdjust.button'),
                                        active: objectPanelOpen,
                                        busy: false,
                                        onClick: () => {
                                            if (isDualMode || !displayImage) return;
                                            if (faceSelectMode) cancelFaceSelect();
                                            setObjectPanelOpen((v) => !v);
                                        },
                                        icon: <IconUserScan size={28} style={{ color: '#64748b' }} />,
                                    },
                                    {
                                        key: 'transparent',
                                        label: t('imageManualEditor.transparentTool'),
                                        active: bg === 'transparent',
                                        busy: false,
                                        onClick: () => handleColorClick('transparent'),
                                        icon: <CheckerIcon id="mtool-transparent" size={34} />,
                                    },
                                    {
                                        key: 'upscale',
                                        label: t('imageManualEditor.upscaleTool'),
                                        active: false,
                                        busy: isUpscaling,
                                        onClick: () => { if (!isDualMode && displayImage) handleUpscale(); },
                                        icon: <IconArrowsMaximize size={28} style={{ color: '#64748b' }} />,
                                    },
                                    {
                                        key: 'eraser',
                                        label: t('imageManualEditor.eraserOpen'),
                                        active: false,
                                        busy: false,
                                        onClick: () => { if (displayImage || (isDualMode && (mergeLeft || mergeRight))) handleOpenEraserModal(); },
                                        icon: <IconEdit size={28} style={{ color: '#64748b' }} />,
                                    },
                                ].map((tool) => (
                                    <Tooltip key={tool.key}>
                                        <TooltipTrigger asChild>
                                            <div
                                                style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', cursor: tool.busy ? 'not-allowed' : 'pointer', opacity: tool.busy ? 0.7 : 1 }}
                                                onClick={tool.busy ? undefined : tool.onClick}
                                            >
                                                <div style={{
                                                    width: '100%', aspectRatio: '1', borderRadius: 8,
                                                    border: `2px solid ${(tool.active || tool.busy) ? '#fe9a00' : 'var(--color-border, #e5e7eb)'}`,
                                                    boxShadow: (tool.active || tool.busy) ? '0 0 0 2px #ffe4b0' : 'none',
                                                    backgroundColor: 'var(--color-surface, #f3f4f6)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'all 0.15s', padding: 3,
                                                }}>
                                                    {tool.busy ? (
                                                        <span style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #fe9a0044', borderTopColor: '#fe9a00', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                                                    ) : tool.icon}
                                                </div>
                                                <span style={{ color: (tool.active || tool.busy) ? '#fe9a00' : '#9ca3af', lineHeight: 1.15, fontSize: 10, width: '100%', textAlign: 'center' }}>{tool.label}</span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{tool.label}</TooltipContent>
                                    </Tooltip>
                                ))}
                            </div>
                            {/* Object tool — Face / Skin / Hair, inline so the photo frame
                                itself shows the live preview while sliders move */}
                            {objectPanelOpen && displayImage && !isDualMode && (
                                <ObjectAdjustPanel
                                    key={displayImage}
                                    imageSrc={displayImage}
                                    onPreview={setObjectPreviewUrl}
                                    onBrush={setObjectBrush}
                                    onApply={handleObjectApply}
                                    onClose={() => setObjectPanelOpen(false)}
                                    t={t}
                                />
                            )}

                            {/* Manual face-area fallback — for when auto-detect picks the wrong spot */}
                            {displayImage && !isDualMode && !isFaceEnhancing && !objectPanelOpen && (
                                <button
                                    onClick={faceSelectMode ? cancelFaceSelect : enterFaceSelect}
                                    style={{ marginTop: 6, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5, fontWeight: 600, color: faceSelectMode ? '#fe9a00' : '#9ca3af', textAlign: 'left', textDecoration: 'underline', textUnderlineOffset: 2 }}
                                >
                                    {faceSelectMode ? t('imageManualEditor.eraserCancel', 'Cancel') : t('faceEnhance.selectArea')}
                                </button>
                            )}
                        </div>

                        {/* The "Cutout Editor" (আরো এডিট করুন) tool now lives in the AI Tools
                            grid above, alongside face enhance / bg remove / transparent. */}

                        {/* Stays at the card's bottom edge even when the sidebar scrolls */}
                        {removeBgButton && (
                            <div className="sticky bottom-0 z-10 mt-auto -mx-4 -mb-4 bg-[var(--mantine-color-body)] px-4 pb-4 pt-2">
                                {removeBgButton}
                            </div>
                        )}
                    </div>
                </div>

                {/* ══ CENTER PREVIEW (order:2 — from styles.centerPreview) ══ */}
                <div
                    className={styles.centerPreview}
                    ref={stageRef}
                    onPointerDown={displayImage && (!isDualMode || !!processedUrl) ? handlePointerDown : undefined}
                    onPointerMove={displayImage && (!isDualMode || !!processedUrl) ? handlePointerMove : undefined}
                    onPointerUp={displayImage && (!isDualMode || !!processedUrl) ? handlePointerUp : undefined}
                    onPointerCancel={displayImage && (!isDualMode || !!processedUrl) ? handlePointerUp : undefined}
                    onDragOver={!isDualMode && !displayImage ? (e) => { e.preventDefault(); setIsDraggingOver(true); } : undefined}
                    onDragLeave={!isDualMode && !displayImage ? () => setIsDraggingOver(false) : undefined}
                    onDrop={!isDualMode && !displayImage ? handleDrop : undefined}
                    onClick={!isDualMode && !displayImage ? () => fileInputRef.current?.click() : undefined}
                    style={{ cursor: displayImage && (!isDualMode || !!processedUrl) ? (dragRef.current ? 'grabbing' : 'grab') : (!isDualMode && !displayImage ? 'pointer' : 'default'), userSelect: 'none', WebkitUserSelect: 'none', touchAction: displayImage && (!isDualMode || !!processedUrl) ? 'none' : undefined }}
                >
                    {/* ── DUAL MODE: two upload zones ── */}
                    {isDualMode && !displayImage && (
                        <div style={{ padding: 16, width: '100%', position: 'relative' }}>
                            {/* Back to single mode button — mobile only */}
                            {isMobile && (
                                <button
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (mergeLeft) setPreviewUrl(mergeLeft.url);
                                        setMergeLeft(null); setMergeRight(null);
                                        setProcessedUrl(null); setBgRemoved(false);
                                        setSize('bd_passport'); setVisaCountryLabel('');
                                    }}
                                    style={{ position: 'absolute', top: 4, right: 4, zIndex: 5, display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 20, border: 'none', background: 'rgba(220,38,38,0.85)', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                                >
                                    <IconX size={11} />
                                    একক মোড
                                </button>
                            )}
                            {/* Loading overlay shown during bg removal — keeps dual panels visible at same zoom */}
                            {isRemovingBg && (
                                <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(0,0,0,0.45)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: 16 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '8px 18px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                                        <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                        <span ref={bgProgressLabelRef} />
                                    </div>
                                </div>
                            )}
                            {/* Dual upload zones */}
                            <div style={{ width: '100%', border: '2px dashed #4a4e57', borderRadius: 12, overflow: 'hidden', display: 'flex', gap: 0, pointerEvents: isRemovingBg ? 'none' : 'auto', opacity: isRemovingBg ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                                {/* Left zone */}
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div
                                        ref={leftZoneRef}
                                        style={{ width: '100%', aspectRatio: `${sizeConfig.wmm / 2}/${sizeConfig.hmm}`, background: mergeLeft ? getVisualBg() : '#2c2e33', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: mergeLeft ? 'grab' : 'pointer', position: 'relative', touchAction: 'none' }}
                                        onClick={!mergeLeft ? () => inlineMergeLeftRef.current?.click() : undefined}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files?.[0]; if (f && isUnsupportedUpload(f)) { notifications.show({ message: t('imageManualEditor.avifNotSupported'), color: 'red' }); return; } if (f?.type.startsWith('image/')) { setMergeLeft({ url: URL.createObjectURL(f), offsetX: 0, offsetY: 0, zoom: 1 }); } }}
                                        onPointerDown={mergeLeft ? (e) => handleZonePanStart(e, 'left') : undefined}
                                        onPointerMove={mergeLeft ? (e) => handleZonePanMove(e, 'left') : undefined}
                                        onPointerUp={mergeLeft ? handleZonePanEnd : undefined}
                                        onPointerCancel={mergeLeft ? handleZonePanEnd : undefined}
                                    >
                                        {mergeLeft ? (
                                            <>
                                                <img src={mergeLeft.url} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', transform: `translate(${mergeLeft.offsetX ?? 0}px, ${mergeLeft.offsetY ?? 0}px) scale(${mergeLeft.zoom ?? 1})`, transformOrigin: 'center center', pointerEvents: 'none', filter: `brightness(${brightness/100}) contrast(${contrast/100}) saturate(${saturation/100})${sharp > 0 ? ' url(#gim-sharpen)' : ''}` }} alt="left" />
                                                <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#dc2626', color: 'white', position: 'absolute', top: 6, right: 6, zIndex: 2 }} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setMergeLeft(null); setProcessedUrl(null); setBgRemoved(false); }}>
                                                    <IconX size={12} />
                                                </button>
                                                <div onPointerDown={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.95)', borderRadius: 100, boxShadow: '0 2px 6px rgba(0,0,0,0.2)', overflow: 'hidden', height: 20 }}>
                                                    <button onClick={(e) => { e.stopPropagation(); handleZoomSide('left', -0.25); }} style={{ width: 20, height: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#9ca3af', padding: 0, lineHeight: '20px', textAlign: 'center' }}>−</button>
                                                    <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', minWidth: 24, height: 20, lineHeight: '20px', textAlign: 'center', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>{Math.round((mergeLeft.zoom ?? 1) * 100)}%</span>
                                                    <button onClick={(e) => { e.stopPropagation(); handleZoomSide('left', 0.25); }} style={{ width: 20, height: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#9ca3af', padding: 0, lineHeight: '20px', textAlign: 'center' }}>+</button>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', padding: '0 12px' }}>
                                                <IconUpload size={20} color="#d1d5db" />
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#d1d5db', textAlign: 'center' }}>{t('imageGeneration.mergeLeftUpload', 'বাম দিকের ছবি')}</span>
                                            </div>
                                        )}
                                    </div>
                                    <input ref={inlineMergeLeftRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" style={{ display: 'none' }} onChange={(e) => handleMergePickSide('left', e)} />
                                </div>

                                {/* Right zone */}
                                <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                    <div
                                        ref={rightZoneRef}
                                        style={{ width: '100%', aspectRatio: `${sizeConfig.wmm / 2}/${sizeConfig.hmm}`, background: mergeRight ? getVisualBg() : '#2c2e33', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: mergeRight ? 'grab' : 'pointer', position: 'relative', touchAction: 'none' }}
                                        onClick={!mergeRight ? () => inlineMergeRightRef.current?.click() : undefined}
                                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const f = e.dataTransfer.files?.[0]; if (f && isUnsupportedUpload(f)) { notifications.show({ message: t('imageManualEditor.avifNotSupported'), color: 'red' }); return; } if (f?.type.startsWith('image/')) { setMergeRight({ url: URL.createObjectURL(f), offsetX: 0, offsetY: 0, zoom: 1 }); } }}
                                        onPointerDown={mergeRight ? (e) => handleZonePanStart(e, 'right') : undefined}
                                        onPointerMove={mergeRight ? (e) => handleZonePanMove(e, 'right') : undefined}
                                        onPointerUp={mergeRight ? handleZonePanEnd : undefined}
                                        onPointerCancel={mergeRight ? handleZonePanEnd : undefined}
                                    >
                                        {mergeRight ? (
                                            <>
                                                <img src={mergeRight.url} draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', transform: `translate(${mergeRight.offsetX ?? 0}px, ${mergeRight.offsetY ?? 0}px) scale(${mergeRight.zoom ?? 1})`, transformOrigin: 'center center', pointerEvents: 'none', filter: `brightness(${brightness/100}) contrast(${contrast/100}) saturate(${saturation/100})${sharp > 0 ? ' url(#gim-sharpen)' : ''}` }} alt="right" />
                                                <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer', background: '#dc2626', color: 'white', position: 'absolute', top: 6, right: 6, zIndex: 2 }} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setMergeRight(null); setProcessedUrl(null); setBgRemoved(false); }}>
                                                    <IconX size={12} />
                                                </button>
                                                <div onPointerDown={(e) => e.stopPropagation()} style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', zIndex: 2, display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.95)', borderRadius: 100, boxShadow: '0 2px 6px rgba(0,0,0,0.2)', overflow: 'hidden', height: 20 }}>
                                                    <button onClick={(e) => { e.stopPropagation(); handleZoomSide('right', -0.25); }} style={{ width: 20, height: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#9ca3af', padding: 0, lineHeight: '20px', textAlign: 'center' }}>−</button>
                                                    <span style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', minWidth: 24, height: 20, lineHeight: '20px', textAlign: 'center', borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>{Math.round((mergeRight.zoom ?? 1) * 100)}%</span>
                                                    <button onClick={(e) => { e.stopPropagation(); handleZoomSide('right', 0.25); }} style={{ width: 20, height: 20, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#9ca3af', padding: 0, lineHeight: '20px', textAlign: 'center' }}>+</button>
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', padding: '0 12px' }}>
                                                <IconUpload size={20} color="#d1d5db" />
                                                <span style={{ fontSize: 12, fontWeight: 500, color: '#d1d5db', textAlign: 'center' }}>{t('imageGeneration.mergeRightUpload', 'ডান দিকের ছবি')}</span>
                                            </div>
                                        )}
                                    </div>
                                    <input ref={inlineMergeRightRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" style={{ display: 'none' }} onChange={(e) => handleMergePickSide('right', e)} />
                                </div>
                            </div>

                            {/* Swap button */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
                                <button
                                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: (mergeLeft || mergeRight) ? 'pointer' : 'default', background: '#9ca3af', color: '#e5e7eb' }}
                                    disabled={!mergeLeft && !mergeRight}
                                    onClick={() => { const tmp = mergeLeft; setMergeLeft(mergeRight); setMergeRight(tmp); }}
                                >
                                    <IconArrowsExchange size={22} />
                                </button>
                            </div>

                            {mergeLeft && mergeRight && (
                                <p style={{ textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.55)', marginTop: 8 }}>
                                    {t('imageGeneration.dualReadyHint', 'উভয় ছবি প্রস্তুত')}
                                </p>
                            )}
                        </div>
                    )}


                    {/* ── SINGLE MODE (also dual after BG removal): photo display ── */}
                    {displayImage && (
                        <>
                        {/* Bottom padding reserves the pinned toolbar's space, which is
                            no longer part of this column's flow: 38px of bar, its 12px
                            offset from the stage floor, and 14px of air above it. */}
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: 16, paddingBottom: 64, width: '100%', overflow: 'hidden', margin: 'auto' }}>
                            {/* Framed photo */}
                            <div style={{ position: 'relative', display: 'inline-flex' }}>
                                {/* Close / remove photo button */}
                                {/* Feather erase — same row as close button */}
                                {bgRemoved && processedUrl && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                onPointerDown={(e) => e.stopPropagation()}
                                                onClick={(e) => { e.stopPropagation(); handleOpenEraserModal(); }}
                                                style={{
                                                    position: 'absolute', top: -10, right: 24, zIndex: 10,
                                                    width: 28, height: 28, borderRadius: '50%',
                                                    background: '#fe9a00', border: '2px solid rgba(255,255,255,0.2)',
                                                    color: '#fff', display: 'flex', alignItems: 'center',
                                                    justifyContent: 'center', cursor: 'pointer',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                                }}
                                            >
                                                <IconEdit size={14} strokeWidth={2.2} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom">{t('imageManualEditor.eraserOpen')}</TooltipContent>
                                    </Tooltip>
                                )}
                                {/* Remove photo */}
                                <button
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPreviewUrl(null);
                                        setProcessedUrl(null);
                                        setBgRemoved(false);
                                        setAutoEnhanced(false);
                                        setPreEnhanceVals(null);
                                        setZoom(1);
                                        setOffset({ x: 0, y: 0 });
                                        setRotation(0);
                                        setCurrentEditId(null);
                                        setSavedToHistory(false);
                                        setSavedImageInfo(null);
                                        setSavedFileName('');
                                        setNameEditing(false);
                                        skipBlockerRef.current = true;
                                        navigate('?', { replace: true });
                                        skipBlockerRef.current = false;
                                    }}
                                    style={{
                                        position: 'absolute', top: -10, right: -10, zIndex: 10,
                                        width: 28, height: 28, borderRadius: '50%',
                                        background: '#dc2626', border: '2px solid rgba(255,255,255,0.2)',
                                        color: 'white', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', cursor: 'pointer',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                                    }}
                                >
                                    <IconX size={14} strokeWidth={2.5} />
                                </button>
                            <div
                                ref={frameRef}
                                style={{
                                    ...(isRemovingBg ? { background: 'transparent' } : frameBgStyle()),
                                    borderRadius: 8,
                                    overflow: 'hidden',
                                    clipPath: 'inset(0 round 8px)',
                                    boxShadow: '0 6px 32px rgba(0,0,0,0.4)',
                                    maxWidth: '100%',
                                    maxHeight: 520,
                                    aspectRatio: isFreeSize ? freeAspect : `${sizeConfig.wmm} / ${sizeConfig.hmm}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                }}
                            >
                                <img
                                    ref={previewImgRef}
                                    src={objectPreviewUrl || editedPreviewUrl || displayImage}
                                    alt=""
                                    draggable={false}
                                    onLoad={(e) => {
                                        const img = e.currentTarget;
                                        if (pendingOffsetRestoreRef.current && img.naturalWidth > 0) {
                                            const fW = frameRef.current?.clientWidth || img.naturalWidth;
                                            const fH = frameRef.current?.clientHeight || img.naturalHeight;
                                            const cs = Math.min(fW / img.naturalWidth, fH / img.naturalHeight);
                                            const { offsetX, offsetY } = pendingOffsetRestoreRef.current;
                                            pendingOffsetRestoreRef.current = null;
                                            setOffset({ x: offsetX * img.naturalWidth * cs, y: offsetY * img.naturalHeight * cs });
                                        }
                                    }}
                                    style={{
                                        maxWidth: '100%', maxHeight: '100%', objectFit: 'contain',
                                        transform: `translate(${offset.x}px,${offset.y}px) scale(${zoom}) rotate(${rotation}deg)`,
                                        transformOrigin: 'center center',
                                        // Its own compositor layer: without this every
                                        // zoom/rotate step repaints the photo on the main thread.
                                        willChange: 'transform',
                                        filter: `brightness(${brightness / 100}) contrast(${contrast / 100}) saturate(${saturation / 100})${sharp > 0 ? ' url(#gim-sharpen)' : ''}`,
                                        // Drags switch this off inline (beginViewDrag) so they
                                        // track the pointer, then put it back on release.
                                        transition: PREVIEW_TRANSITION,
                                        pointerEvents: 'none',
                                        opacity: (isRemovingBg || isFaceEnhancing || isUpscaling) ? 0.45 : 1,
                                    }}
                                />
                                {/* Selection brush capture surface. Sits over the
                                    whole frame and converts each pointer position
                                    back through the preview transform, so painting
                                    stays accurate at any zoom, pan or rotation. */}
                                {objectBrush && (
                                    <div
                                        onPointerDown={handleBrushDown}
                                        onPointerMove={handleBrushMove}
                                        onPointerUp={handleBrushUp}
                                        onPointerCancel={handleBrushUp}
                                        onPointerLeave={() => {
                                            handleBrushUp();
                                            if (brushCursorRef.current) brushCursorRef.current.style.opacity = '0';
                                            if (loupeRef.current) loupeRef.current.style.opacity = '0';
                                        }}
                                        style={{
                                            position: 'absolute', inset: 0, zIndex: 5,
                                            cursor: 'crosshair', touchAction: 'none',
                                        }}
                                    />
                                )}
                                {objectBrush && (
                                    <span
                                        ref={brushCursorRef}
                                        aria-hidden="true"
                                        style={{
                                            position: 'fixed', left: 0, top: 0, opacity: 0,
                                            pointerEvents: 'none', zIndex: 6, borderRadius: '50%',
                                            border: `2px solid ${objectBrush.mode === 'erase' ? '#f87171' : '#38bdf8'}`,
                                            boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                                        }}
                                    />
                                )}
                                {objectBrush && (
                                    <canvas
                                        ref={loupeRef}
                                        aria-hidden="true"
                                        style={{
                                            position: 'fixed', left: 0, top: 0, opacity: 0,
                                            width: LOUPE_SIZE, height: LOUPE_SIZE,
                                            pointerEvents: 'none', zIndex: 7, borderRadius: '50%',
                                            border: '2px solid rgba(255,255,255,0.85)',
                                            boxShadow: '0 6px 20px rgba(0,0,0,0.45)',
                                        }}
                                    />
                                )}
                                {/* BG removal overlay */}
                                {isRemovingBg && (
                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none', zIndex: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.72)', color: '#fff', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                            <span ref={bgProgressLabelRef} />
                                        </div>
                                    </div>
                                )}
                                {/* AI face enhance overlay — renders progress inline over the preview */}
                                {isFaceEnhancing && (
                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none', zIndex: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.72)', color: '#fff', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                            {t('faceEnhance.processing')}
                                        </div>
                                    </div>
                                )}
                                {/* Upscale overlay — the model can take a while, so the
                                    photo dims and says what is happening. */}
                                {isUpscaling && (
                                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', pointerEvents: 'none', zIndex: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(0,0,0,0.72)', color: '#fff', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                            {t('upscaleImage.upscaling')}
                                        </div>
                                    </div>
                                )}
                                {/* Manual face-area selection overlay */}
                                {faceSelectMode && faceRect && (
                                    <div
                                        onPointerMove={onFaceDrag}
                                        onPointerUp={endFaceDrag}
                                        onPointerLeave={endFaceDrag}
                                        style={{ position: 'absolute', inset: 0, zIndex: 6, touchAction: 'none' }}
                                    >
                                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)', pointerEvents: 'none' }} />
                                        <div
                                            onPointerDown={(e) => startFaceDrag(e, 'move')}
                                            style={{ position: 'absolute', left: faceRect.x, top: faceRect.y, width: faceRect.w, height: faceRect.h, border: '2px solid #fe9a00', background: 'rgba(254,154,0,0.06)', boxShadow: '0 0 0 1px rgba(0,0,0,0.3)', cursor: 'move' }}
                                        >
                                            {['nw', 'ne', 'sw', 'se'].map((c) => (
                                                <div
                                                    key={c}
                                                    onPointerDown={(e) => startFaceDrag(e, c)}
                                                    style={{ position: 'absolute', width: 16, height: 16, background: '#fe9a00', borderRadius: 4, border: '2px solid #fff', ...(c.includes('n') ? { top: -8 } : { bottom: -8 }), ...(c.includes('w') ? { left: -8 } : { right: -8 }), cursor: `${c}-resize` }}
                                                />
                                            ))}
                                        </div>
                                        {/* Action bar */}
                                        <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                                            <button onClick={cancelFaceSelect} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.9)', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                                {t('imageManualEditor.eraserCancel', 'Cancel')}
                                            </button>
                                            <button onClick={confirmFaceSelect} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#fe9a00', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(254,154,0,0.5)' }}>
                                                {t('faceEnhance.enhanceArea')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            </div>{/* end frame wrapper */}

                        </div>
                            {/* Zoom / rotate toolbar — pinned to the bottom of the
                                dark stage rather than sitting under the photo, so it
                                stays in the same place whatever the photo's shape. */}
                            <div
                                style={{
                                    position: 'absolute', bottom: 12, left: '50%',
                                    transform: 'translateX(-50%)', zIndex: 8,
                                    opacity: isRemovingBg ? 0.35 : 1,
                                    pointerEvents: isRemovingBg ? 'none' : 'auto',
                                    transition: 'opacity 0.2s', maxWidth: 'calc(100% - 24px)',
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                onPointerUp={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                            >
                            {isMobile ? (
                                /* ── Mobile toolbar: single row of buttons, no sliders ── */
                                <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center', gap: 4, background: 'rgba(0,0,0,0.82)', borderRadius: 22, padding: '6px 10px', maxWidth: '100%' }}>
                                    {/* Zoom out / % / in */}
                                    <button type="button" title={t('imageManualEditor.zoomOut', 'ছোট করুন')} onClick={() => setZoom(z => clamp(z - 0.1, ZOOM_MIN, ZOOM_MAX))}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconZoomOut size={16} />
                                    </button>
                                    <span onClick={() => setZoom(1)} style={{ fontSize: 11, fontWeight: 600, color: zoom !== 1 ? '#fe9a00' : 'rgba(255,255,255,0.8)', minWidth: 34, textAlign: 'center', cursor: 'pointer', userSelect: 'none', fontVariantNumeric: 'tabular-nums', flexShrink:0 }}>
                                        {Math.round(zoom * 100)}%
                                    </span>
                                    <button type="button" title={t('imageManualEditor.zoomIn', 'বড় করুন')} onClick={() => setZoom(z => clamp(z + 0.1, ZOOM_MIN, ZOOM_MAX))}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconZoomIn size={16} />
                                    </button>
                                    <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.22)', margin: '0 2px', flexShrink:0 }} />
                                    {/* Rotate −90 / ° / +90 */}
                                    <button type="button" title={t('imageManualEditor.rotateMinus90', '−৯০° ঘোরান')} onClick={() => setRotation(r => r - 90)}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconRotate2 size={16} />
                                    </button>
                                    <span onClick={() => setRotation(0)} style={{ fontSize: 11, fontWeight: 600, color: rotation !== 0 ? '#fe9a00' : 'rgba(255,255,255,0.8)', minWidth: 30, textAlign: 'center', cursor: 'pointer', userSelect: 'none', fontVariantNumeric: 'tabular-nums', flexShrink:0 }}>
                                        {rotation > 0 ? `+${rotation}°` : `${rotation}°`}
                                    </span>
                                    <button type="button" title={t('imageManualEditor.rotatePlus90', '+৯০° ঘোরান')} onClick={() => setRotation(r => r + 90)}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0, transform:'scaleX(-1)' }}>
                                        <IconRotate2 size={16} />
                                    </button>
                                    <span style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.22)', margin: '0 2px', flexShrink:0 }} />
                                    {/* Reset view / change photo */}
                                    <button type="button" title={t('imageManualEditor.resetView', 'রিসেট')} onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); setRotation(0); }}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconRefresh size={16} />
                                    </button>
                                    <button type="button" title={t('imageManualEditor.changePhoto', 'ছবি পরিবর্তন')} onClick={() => fileInputRef.current?.click()}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:30, height:30, borderRadius:8, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconUpload size={16} />
                                    </button>
                                </div>
                            ) : (
                                /* ── Desktop full toolbar. One row always: the two
                                   sliders absorb a narrow stage by shrinking, which
                                   reads better than the bar folding in half. ── */
                                <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', justifyContent: 'center', gap: 6, background: 'rgba(0,0,0,0.82)', borderRadius: 24, padding: '6px 12px', maxWidth: '100%' }}>
                                    <button type="button" onClick={() => setZoom(z => clamp(z - 0.1, ZOOM_MIN, ZOOM_MAX))}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconZoomOut size={13} />
                                    </button>
                                    {/* Uncontrolled on purpose: the thumb is driven by the
                                        DOM during a drag and re-synced from state after. */}
                                    <input
                                        ref={zoomSliderRef}
                                        type="range" min={ZOOM_MIN * 100} max={ZOOM_MAX * 100} step={1}
                                        defaultValue={Math.round(zoom * 100)}
                                        onPointerDown={beginViewDrag}
                                        onInput={(e) => {
                                            beginViewDrag();
                                            liveViewRef.current.zoom = clamp(Number(e.target.value) / 100, ZOOM_MIN, ZOOM_MAX);
                                            paintView();
                                        }}
                                        onPointerUp={endViewDrag}
                                        onPointerCancel={endViewDrag}
                                        onBlur={endViewDrag}
                                        onKeyUp={endViewDrag}
                                        style={{ flex: '1 1 72px', minWidth: 44, maxWidth: 88, accentColor: '#fe9a00', cursor: 'pointer' }}
                                    />
                                    <button type="button" onClick={() => setZoom(z => clamp(z + 0.1, ZOOM_MIN, ZOOM_MAX))}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'center', width:22, height:22, borderRadius:5, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', flexShrink:0 }}>
                                        <IconZoomIn size={13} />
                                    </button>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            {/* Text is written by paintView, never by React —
                                                a manual textContent swap detaches the text node
                                                React tracks, and later updates go nowhere. */}
                                            <span ref={zoomLabelRef} onClick={() => setZoom(1)} style={{ fontSize: 11, color: zoom !== 1 ? '#fe9a00' : 'rgba(255,255,255,0.75)', minWidth: 36, textAlign: 'center', fontVariantNumeric: 'tabular-nums', cursor: 'pointer', userSelect: 'none' }} />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.resetZoom', 'ক্লিক করে ১০০% করুন')}</TooltipContent>
                                    </Tooltip>
                                    <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.25)', margin: '0 2px' }} />
                                    <input
                                        ref={rotationSliderRef}
                                        type="range" min={-180} max={180} step={1}
                                        defaultValue={rotation}
                                        onPointerDown={beginViewDrag}
                                        onInput={(e) => {
                                            beginViewDrag();
                                            liveViewRef.current.rotation = Number(e.target.value);
                                            paintView();
                                        }}
                                        onPointerUp={endViewDrag}
                                        onPointerCancel={endViewDrag}
                                        onBlur={endViewDrag}
                                        onKeyUp={endViewDrag}
                                        style={{ flex: '1 1 72px', minWidth: 44, maxWidth: 88, accentColor: '#fe9a00', cursor: 'pointer' }}
                                    />
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span ref={rotationLabelRef} onClick={() => setRotation(0)} style={{ fontSize: 11, color: rotation !== 0 ? '#fe9a00' : 'rgba(255,255,255,0.75)', minWidth: 30, textAlign: 'center', fontVariantNumeric: 'tabular-nums', cursor: 'pointer', userSelect: 'none' }} />
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.resetRotation', 'ক্লিক করে ০° করুন')}</TooltipContent>
                                    </Tooltip>
                                    <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.25)', margin: '0 2px' }} />
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={() => setRotation(r => r - 90)} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer' }}>
                                                <IconRotate2 size={15} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.rotateMinus90', '−৯০° ঘোরান')}</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={() => setRotation(r => r + 90)} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer', transform:'scaleX(-1)' }}>
                                                <IconRotate2 size={15} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.rotatePlus90', '+৯০° ঘোরান')}</TooltipContent>
                                    </Tooltip>
                                    <span style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.25)', margin: '0 2px' }} />
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={() => { setZoom(1); setOffset({ x: 0, y: 0 }); setRotation(0); }} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer' }}>
                                                <IconRefresh size={15} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.resetView')}</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button type="button" onClick={() => fileInputRef.current?.click()} style={{ display:'flex', alignItems:'center', justifyContent:'center', width:26, height:26, borderRadius:6, border:'none', background:'rgba(255,255,255,0.12)', color:'#fff', cursor:'pointer' }}>
                                                <IconUpload size={15} />
                                            </button>
                                        </TooltipTrigger>
                                        <TooltipContent side="top">{t('imageManualEditor.changePhoto')}</TooltipContent>
                                    </Tooltip>
                                </div>
                            )}
                            </div>
                        </>
                    )}

                    {/* ── SINGLE MODE: loading skeleton (fetching from history) ── */}
                    {!isDualMode && !displayImage && isLoadingFromHistory && (
                        <div className={styles.uploadArea} style={{ cursor: 'default', gap: 14, pointerEvents: 'none' }}>
                            <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', animation: 'pulse 1.4s ease-in-out infinite' }} />
                            <div style={{ width: '60%', height: 14, borderRadius: 8, background: 'rgba(255,255,255,0.12)', animation: 'pulse 1.4s ease-in-out 0.1s infinite' }} />
                            <div style={{ width: '80%', height: 10, borderRadius: 8, background: 'rgba(255,255,255,0.08)', animation: 'pulse 1.4s ease-in-out 0.2s infinite' }} />
                            <div style={{ width: '45%', height: 10, borderRadius: 8, background: 'rgba(255,255,255,0.08)', animation: 'pulse 1.4s ease-in-out 0.3s infinite' }} />
                            <div style={{ marginTop: 4, width: 120, height: 34, borderRadius: 8, background: 'rgba(255,255,255,0.1)', animation: 'pulse 1.4s ease-in-out 0.15s infinite' }} />
                        </div>
                    )}

                    {/* ── SINGLE MODE: empty upload state ── */}
                    {!isDualMode && !displayImage && !isLoadingFromHistory && (
                        isMobile ? (
                            /* ── Mobile: wide card design matching /generate-image ── */
                            <div style={{ display:'flex', flexDirection:'column', width:'100%', flex:1, alignSelf:'stretch', minHeight:'70vh', padding:'24px 16px 20px', overflow:'hidden', justifyContent:'center' }}>
                                {/* Upload box */}
                                <div
                                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                    style={{ border:'1.5px solid rgba(255,255,255,0.12)', borderRadius:20, background:'rgba(255,255,255,0.04)', padding:'40px 20px 34px', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', gap:12, cursor:'pointer', flexShrink:0 }}
                                >
                                    {/* Icon circle */}
                                    <div style={{ width:62, height:62, borderRadius:'50%', background:'rgba(255,255,255,0.09)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                        <IconPhotoUp size={28} color="rgba(255,255,255,0.65)" />
                                    </div>
                                    <div style={{ textAlign:'center' }}>
                                        <p style={{ margin:0, fontSize:15, fontWeight:700, color:'#fff', lineHeight:1.4 }}>{t('imageGeneration.mobileUploadBoxTitle')}</p>
                                        <p style={{ margin:'5px 0 0', fontSize:11.5, color:'rgba(255,255,255,0.4)', lineHeight:1.5 }}>{t('imageGeneration.mobileUploadBoxHint')}</p>
                                    </div>
                                    {/* Orange upload button */}
                                    <div style={{ marginTop:4, display:'flex', alignItems:'center', justifyContent:'center', gap:7, padding:'11px 28px', borderRadius:12, background:'#e68a00', color:'#fff', fontSize:14, fontWeight:700, flexShrink:0 }}>
                                        <IconUpload size={16} />
                                        {t('imageGeneration.uploadButton')}
                                    </div>
                                </div>

                                {/* Divider */}
                                <div style={{ display:'flex', alignItems:'center', gap:10, margin:'16px 0' }}>
                                    <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }} />
                                    <span style={{ fontSize:12, color:'rgba(255,255,255,0.35)', fontWeight:500 }}>{t('imageGeneration.mobileUploadOrDivider', 'অথবা')}</span>
                                    <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.1)' }} />
                                </div>

                                {/* Dual upload button */}
                                <button
                                    onClick={(e) => { e.stopPropagation(); const newSize = 'dual_image'; setProcessedUrl(null); setBgRemoved(false); setAutoEnhanced(false); setPreEnhanceVals(null); setSize(newSize); setVisaCountryLabel(''); }}
                                    style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:9, padding:'14px 16px', borderRadius:16, border:'none', background:'linear-gradient(135deg,#065f46,#059669)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', boxShadow:'0 4px 14px rgba(5,150,105,0.4)', width:'100%', flexShrink:0 }}
                                >
                                    <IconUsers size={20} />
                                    {t('imageGeneration.dualUploadButton', '২ টি আলাদা ছবি আপলোড করুন।')}
                                </button>
                            </div>
                        ) : (
                            /* ── Desktop: dashed card — matches the selected photo-size aspect ── */
                            <div
                                className={`${styles.uploadArea} ${isDraggingOver ? styles.dragging : ''}`}
                                onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                style={{ aspectRatio: isFreeSize ? '4 / 3' : `${sizeConfig.wmm} / ${sizeConfig.hmm}`, maxWidth: (isFreeSize || sizeConfig.wmm > sizeConfig.hmm) ? 480 : 350, maxHeight: 520, alignSelf: 'flex-start', marginTop: 24 }}
                            >
                                <div className={styles.uploadIconWrap}>
                                    <IconPhoto size={32} className={styles.uploadIcon} />
                                </div>
                                <p style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#fff', textAlign: 'center', lineHeight: 1.4 }}>
                                    {t('imageGeneration.uploadInstruction')}
                                </p>
                                {/* Hint text with the selected photo-size (mm) badge inline, same line */}
                                <p style={{ margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.5)', textAlign: 'center', lineHeight: 1.7 }}>
                                    {t('imageGeneration.uploadHint')}
                                    <span style={{ display: 'inline-block', verticalAlign: 'middle', marginLeft: 8, padding: '2px 10px', borderRadius: 999, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', color: 'rgba(255,255,255,0.75)', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
                                        {isFreeSize ? t('imageManualEditor.freeSize') : `${sizeConfig.wmm} × ${sizeConfig.hmm} mm`}
                                    </span>
                                </p>
                                <div className={styles.uploadBtn}>
                                    <IconUpload size={15} />
                                    {t('imageGeneration.uploadButton')}
                                </div>
                                <p style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                                    <kbd style={{ padding: '2px 6px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>Ctrl+U</kbd>
                                </p>
                            </div>
                        )
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        hidden
                        onChange={(e) => {
                            const f = e.target.files[0];
                            e.target.value = '';
                            if (f) handleFileSelect(f);
                        }}
                    />

                    {/* The edit drawer on the panel's bottom edge (single photo). Its pointer events stay inside it, so dragging a slider
                        never pans the photo underneath. */}
                    {!isDualMode && displayImage && (
                        <div onPointerDown={(e) => e.stopPropagation()} onPointerMove={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                            <PhotoEditDrawer value={edits} onChange={setEdits} open={editorOpen} onClose={() => setEditorOpen(false)} thumbs={filterThumbs} />
                        </div>
                    )}
                </div>

                {/* ══ RIGHT SIDEBAR (order:3) — the AI photo editor's action column once a photo is in (labelled from `xl`, an icon strip
                    below, kept on the right on a tablet); before that the booth inbox + QR card (a QR icon strip below 1024px) ══ */}
                <div className={`${styles.rightSidebar} ${actionColumn ? '!p-1.5 xl:!p-3' : ''}`} style={{ opacity: isRemovingBg ? 0.45 : 1, pointerEvents: isRemovingBg ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                    {actionColumn ? (
                        <div className="flex flex-col items-stretch gap-1 max-xl:rounded-xl max-xl:bg-zinc-100 max-xl:p-1 dark:max-xl:bg-zinc-950 xl:w-[220px] xl:gap-2">
                            {imageLoaded ? (
                                <>
                                    {/* the saved photo's details first, like the AI photo editor */}
                                    {savedImageInfo && <InfoButton compact={compactActions} onOpenChange={loadSavedName}>{savedInfoBody}</InfoButton>}
                                    {!isDualMode && <EditToggle compact={compactActions} tipSide="left" open={editorOpen} edited={editsActive} onClick={() => setEditorOpen((o) => !o)} />}
                                    <ActionBtn compact={compactActions} tipSide="left" icon={btnIcon('/images/photo-tools/copy.svg')} label={t('imageGeneration.copyPhoto')} tooltip={t('imageGeneration.copyTooltip')} onClick={handleCopy} disabled={!exportReady} />
                                    <ActionBtn compact={compactActions} tipSide="left" icon={<IconDownload size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />} label={t('imageManualEditor.download')} onClick={handleDownload} loading={isDownloading} disabled={!exportReady} />
                                    <ActionBtn
                                        compact={compactActions}
                                        tipSide="left"
                                        icon={<IconDeviceFloppy size={19} className="shrink-0 text-primary" />}
                                        label={currentEditId ? t('imageManualEditor.update', 'আপডেট করুন') : (savedToHistory ? t('imageManualEditor.saved') : t('imageManualEditor.save'))}
                                        onClick={handleSave}
                                        loading={isSaving}
                                        disabled={!(isDualMode ? (mergeLeft && mergeRight) : displayImage) || (!currentEditId && savedToHistory)}
                                    />
                                    <PrintButton compact={compactActions} tipSide="left" label={t('imageManualEditor.print')} onClick={exportReady ? handleOpenPrint : undefined} />
                                </>
                            ) : (
                                <ActionBtn compact={compactActions} tipSide="left" icon={<IconQrcode size={19} className="shrink-0 text-zinc-600 dark:text-zinc-300" />} label={t('imageGeneration.qrScanButton')} onClick={openQrModal} />
                            )}
                        </div>
                    ) : (
                    <div className="flex flex-col flex-1 gap-4">

                        {/* Photo Booth queue — customer-submitted photos; a tap loads one onto
                            the canvas. Renders only when the booth is enabled for this user. */}
                        <PhotoBoothInbox onPickFile={(f) => handleFileSelect(f)} onStatusChange={setBoothActive} />

                        {/* ── QR Upload Card — at top on large screen. Hidden while the booth is
                            active (photos arrive via the booth then); shows once it resolves OFF. ── */}
                        {!isCompact && boothActive === false && (
                        <div style={{ borderRadius: 16, background: 'linear-gradient(135deg, #1a1f3a 0%, #1a1f3a 60%, #1a1f3a 100%)', padding: '18px 16px 16px', boxShadow: '0 8px 24px rgba(25,113,194,0.35)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
                            <div style={{ position: 'absolute', bottom: -12, left: -12, width: 56, height: 56, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                                <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid rgba(255,255,255,0.2)' }}>
                                    <IconQrcode size={24} color="#fff" />
                                </div>
                                <div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>{t('imageGeneration.qrUploadButton')}</p>
                                    <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.4, marginTop: 2 }}>{t('imageGeneration.qrUploadLabel')}</p>
                                </div>
                            </div>
                            <button onClick={openQrModal} disabled={isDualMode ? (!!mergeLeft && !!mergeRight) : !!previewUrl} style={{ width: '100%', padding: '9px 16px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: (isDualMode ? (mergeLeft && mergeRight) : previewUrl) ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'background 0.2s ease, border-color 0.2s ease', opacity: (isDualMode ? (mergeLeft && mergeRight) : previewUrl) ? 0.45 : 1 }} onMouseEnter={e => { if (!(isDualMode ? (mergeLeft && mergeRight) : previewUrl)) { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.55)'; } }} onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.35)'; }}>
                                <IconNfc size={15} />
                                {(isDualMode ? (mergeLeft && mergeRight) : previewUrl) ? t('imageManualEditor.photoAlreadyLoaded') : t('imageGeneration.qrScanButton')}
                            </button>
                        </div>
                        )}
                    </div>
                    )}
                </div>
            </div>

            {/* ══ Image History Carousel ══ */}
            {(isLoadingHistory || imageHistory.length > 0) && (
                <div className={styles.historySection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <span className={styles.sectionTitle}>{t('imageManualEditor.recentEdits')}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', flexShrink: 0, background: '#6b7280', color: 'white' }}
                                onClick={scrollHistoryLeft}
                            >‹</button>
                            <button
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: 'none', cursor: 'pointer', flexShrink: 0, background: '#6b7280', color: 'white' }}
                                onClick={scrollHistoryRight}
                            >›</button>
                            {/* the AI photo editor's "view all" — My Files → manual photos */}
                            <Link to="/documents?tab=images&sub=manual" className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold !text-white hover:bg-primary/90">
                                <IconExternalLink size={14} />
                                {t('imageGeneration.viewAllImages')}
                            </Link>
                        </div>
                    </div>
                    <div style={{ overflow: 'hidden', cursor: 'grab', paddingTop: 8, marginTop: -8 }} ref={emblaRef}>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                            {/* First load: no history yet but the request is in flight — render
                                skeleton cards at the fixed card height so the section holds its
                                space instead of appearing from 0 height when data lands. */}
                            {imageHistory.length === 0 && isLoadingHistory
                                ? Array.from({ length: 8 }).map((_, i) => (
                                    <div
                                        key={`history-skeleton-${i}`}
                                        className={styles.historyItem}
                                        style={{ flex: `0 0 ${Math.round(HISTORY_CARD_H * 832 / 1024)}px`, height: `${HISTORY_CARD_H}px`, cursor: 'default' }}
                                    >
                                        <div className={styles.historySkeletonOverlay} />
                                    </div>
                                ))
                                : imageHistory.map((item) => {
                                const sizeKey = item.size || item.photoSize || 'bd_passport';
                                const isDual = sizeKey === 'dual_image';
                                const sizeCfg = sizeConfigs[sizeKey];
                                // Uniform height for every thumbnail; width follows the image aspect.
                                // Before the image loads, fall back to the size config's aspect ratio.
                                const cardH = HISTORY_CARD_H;
                                const fallbackAspect = sizeCfg
                                    ? sizeCfg.exportW / sizeCfg.exportH
                                    : (isDual ? 1062 / 650 : 832 / 1024);
                                const aspect = historyAspects[item.id] ?? fallbackAspect;
                                const cardW = Math.round(cardH * aspect);
                                return (
                                <div
                                    key={item.id}
                                    className={styles.historyItem}
                                    style={{ flex: `0 0 ${cardW}px`, cursor: 'pointer', height: `${cardH}px` }}
                                    onClick={() => { setSelectedHistoryImage(item); setShowPreviewModal(true); }}
                                >
                                    <div className={styles.historySkeletonOverlay} />
                                    <div style={{
                                        position: 'relative', width: '100%', height: '100%',
                                        lineHeight: 0,
                                        ...(() => {
                                            const b = item.background;
                                            if (!b || b === 'image') return { background: '#ffffff' };
                                            if (isSpecialBgToken(b)) return specialBgCss(b) || { background: '#ffffff' };
                                            if (b === 'transparent') return { background: 'repeating-conic-gradient(#ccc 0% 25%,#fff 0% 50%) 0 0/10px 10px' };
                                            return { background: BG_HEX[b] || b };
                                        })(),
                                        borderRadius: 'inherit',
                                        overflow: 'hidden',
                                    }}>
                                        <img
                                            src={item.aiImage}
                                            alt="Edited"
                                            className={styles.historyThumbnail}
                                            onLoad={(e) => {
                                                const img = e.currentTarget;
                                                img.style.opacity = '1';
                                                const sk = img.parentElement?.previousSibling;
                                                if (sk) sk.style.opacity = '0';
                                                const nw = img.naturalWidth;
                                                const nh = img.naturalHeight;
                                                // Record the real aspect so the slot width matches the image
                                                // (uniform height, no blank space) regardless of photo size.
                                                if (nw && nh) {
                                                    const ar = nw / nh;
                                                    setHistoryAspects(prev => prev[item.id] === ar ? prev : { ...prev, [item.id]: ar });
                                                }
                                                // Apply saved crop position using absolute placement inside overflow:hidden
                                                // Only apply when zoom != 1 (matches ImagePreviewModal behaviour — zoom=1 images show full frame)
                                                if (item.zoom && item.zoom !== 1 && (item.offsetX || item.offsetY)) {
                                                    // Use the aspect-derived size (not clientWidth) so the math stays
                                                    // correct after the width re-renders from the recorded aspect.
                                                    const th = HISTORY_CARD_H;
                                                    const tw = nw && nh ? Math.round(th * (nw / nh)) : (img.parentElement?.clientWidth || 120);
                                                    const cs = Math.min(tw / nw, th / nh);
                                                    const sz = item.zoom ?? 1;
                                                    const rw = nw * cs * sz;
                                                    const rh = nh * cs * sz;
                                                    const tx = (tw - rw) / 2 + (item.offsetX ?? 0) * nw * cs;
                                                    const ty = (th - rh) / 2 + (item.offsetY ?? 0) * nh * cs;
                                                    img.style.objectFit = 'none';
                                                    img.style.width = `${rw}px`;
                                                    img.style.height = `${rh}px`;
                                                    img.style.position = 'absolute';
                                                    img.style.left = `${tx}px`;
                                                    img.style.top = `${ty}px`;
                                                }
                                            }}
                                            style={{
                                                opacity: 0,
                                                transition: 'opacity 0.3s ease',
                                                objectFit: isDual ? 'cover' : undefined,
                                            }}
                                        />
                                    </div>
                                </div>
                                );
                                })}
                        </div>
                    </div>
                </div>
            )}

            {/* ══ History Preview Modal (same component as existing page) ══ */}
            <ImagePreviewModal
                opened={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
                images={imageHistory}
                initialImage={selectedHistoryImage}
                hasMore={hasMoreHistory}
                isLoadingMore={isLoadingHistory}
                onLoadMore={() => loadManualHistory(historyPage + 1, true)}
                onPrint={(imageUrl, imageId) => {
                    setPrintBlobUrl(imageUrl);
                    // Free-size photos print FREE (own aspect), not the 4-up passport
                    // layout — read the size off the record being printed.
                    setPrintIsFree((selectedHistoryImage?.size || selectedHistoryImage?.photoSize) === 'free');
                    // Printing a SAVED cut-out straight from history: the master WebP is
                    // transparent, so pass its record id and let PrintModal recover the
                    // stored background token (manual images are absent from PrintModal's
                    // regular-photo history feed, so it must look this up by id).
                    setPrintImageId(imageId ?? null);
                    setShowPrintModal(true);
                    setShowPreviewModal(false);
                }}
                onImageDeleted={(deletedId) => {
                    setImageHistory(prev => prev.filter(img => img.id !== deletedId));
                    setShowPreviewModal(false);
                }}
                onCustomDownload={(imageUrl, imageId, bgKey) => {
                    setCustomDownloadImageUrl(imageUrl);
                    setCustomDownloadImageId(imageId);
                    setCustomDownloadImageBg(bgKey ?? null);
                    setShowCustomDownload(true);
                }}
                onEdit={async (img) => {
                    setShowPreviewModal(false);
                    setIsLoadingFromHistory(true);
                    try {
                        const blob = await imageGenerationService.downloadImage(img.id);
                        const ext = (blob.type.split('/')[1] || 'jpg').split('+')[0];
                        // Store metadata to restore bg state after previewUrl changes
                        pendingHistoryMetaRef.current = {
                            background: img.background,
                            size: img.size || img.photoSize,
                            zoom: img.zoom ?? null,
                            offsetX: img.offsetX ?? null,
                            offsetY: img.offsetY ?? null,
                            manualBgRemoved: img.manualBgRemoved ?? false,
                        };
                        // Enter update mode for this history item
                        setCurrentEditId(manualIdNum(img.id));
                        setSavedImageInfo({ id: img.id, aiImage: img.aiImage || img.url, size: img.size || img.photoSize, background: img.background, savedAt: img.createdAt || img.updatedAt });
                        setSavedFileName(img.fileName || '');
                        setSavedToHistory(true);
                        skipBlockerRef.current = true;
                        navigate(`?id=${img.id}`, { replace: true });
                        skipBlockerRef.current = false;
                        handleFileSelect(new File([blob], `image.${ext}`, { type: blob.type }), { fromHistory: true });
                    } catch (e) {
                        console.error('Failed to load image for editing:', e);
                        notifications.show({ message: t('imageManualEditor.bgRemoveError'), color: 'red' });
                    } finally {
                        setIsLoadingFromHistory(false);
                    }
                }}
            />

            {/* ══ Print Modal ══ */}
            {showPrintModal && (
                <PrintModal
                    opened={showPrintModal}
                    onClose={handleClosePrint}
                    imageUrl={printBlobUrl}
                    imageId={printImageId}
                    isDualImage={size === 'dual_image'}
                    initialSizeType={printIsFree ? 'free' : 'passport'}
                    historySource="manual"
                    imageHistory={imageHistory}
                />
            )}

            {/* ══ Custom Download Modal ══ */}
            {showCustomDownload && (
                <CustomDownloadModal
                    opened={showCustomDownload}
                    onClose={() => setShowCustomDownload(false)}
                    imageUrl={customDownloadImageUrl}
                    imageId={customDownloadImageId}
                    bgKey={customDownloadImageBg}
                    images={imageHistory}
                />
            )}

            {/* ══ QR Modal (exact same UI as existing /generate-image page) ══ */}
            <QrUploadModal
                open={showQrModal}
                onClose={closeQrModal}
                status={qrStatus}
                qrDataUrl={qrDataUrl}
                mobileUrl={qrMobileUrl}
                timeLeft={qrTimeLeft}
                onRefresh={openQrModal}
            />

            {/* ── Feather Erase Modal ── */}
            <EraserModal
                open={eraserModalOpen}
                imageUrl={dualEraserUrl || processedUrl || previewUrl}
                exportW={getExportDims().W}
                exportH={getExportDims().H}
                bgColor={getBgColor()}
                onClose={() => {
                    if (dualEraserUrl) { URL.revokeObjectURL(dualEraserUrl); setDualEraserUrl(null); }
                    setEraserModalOpen(false);
                }}
                onApply={(newUrl, changes = {}) => {
                    if (dualEraserUrl) {
                        URL.revokeObjectURL(dualEraserUrl);
                        setDualEraserUrl(null);
                    }
                    setProcessedUrl(newUrl);
                    setBgRemoved(true);
                    if (changes.bg !== undefined) setBg(changes.bg);
                    if (changes.bgImageSrc !== undefined) setBgImageSrc(changes.bgImageSrc);
                    if (changes.customColor !== undefined) setCustomColor(changes.customColor);
                    if (changes.size !== undefined) { setSize(changes.size); setVisaCountryLabel(''); }
                    if (changes.brightness != null) setBrightness(changes.brightness);
                    if (changes.contrast   != null) setContrast(changes.contrast);
                    if (changes.saturation != null) setSaturation(changes.saturation);
                    if (changes.sharp      != null) setSharp(changes.sharp);
                    if (changes.eraserZoom != null)   setEraserZoom(changes.eraserZoom);
                    if (changes.eraserOffset != null) setEraserOffset(changes.eraserOffset);
                    if (changes.mainEditorZoom != null)   setZoom(clamp(changes.mainEditorZoom, ZOOM_MIN, ZOOM_MAX));
                    if (changes.mainEditorOffset != null) setOffset(changes.mainEditorOffset);
                    setEraserStartFromSaved(false);
                    setEraserModalOpen(false);
                }}
                initialZoom={eraserStartFromSaved && eraserZoom != null ? eraserZoom : zoom}
                initialOffset={eraserStartFromSaved && eraserOffset != null ? eraserOffset : offset}
                mainFrameW={!eraserStartFromSaved ? (frameRef.current?.clientWidth ?? 0) : 0}
                mainFrameH={!eraserStartFromSaved ? (frameRef.current?.clientHeight ?? 0) : 0}
                startAtSavedPosition={eraserStartFromSaved}
                backgrounds={backgrounds}
                initialBg={bg}
                customBgColor={customColor}
                initialBgImageSrc={bgImageSrc}
                photoSizes={photoSizes}
                sizeConfigs={sizeConfigs}
                initialSize={size}
                initialBrightness={brightness}
                initialContrast={contrast}
                initialSaturation={saturation}
                initialSharp={sharp}
                isBgRemoved={bgRemoved}
            />

            {/* AI Face Enhance runs inline (see handleFaceEnhance) and renders in the main preview — no modal. */}

            {/* ── Mobile bottom navigation bar + Sheets ──────────────────── */}
            {isMobile && (
                <>
                    {/* Nav bar */}
                    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border-t border-zinc-200 dark:border-zinc-700 shadow-[0_-4px_24px_rgba(0,0,0,0.12)]">
                        <div className="grid grid-cols-5 items-center px-1 pt-1 pb-2">

                            {/* Photo Size */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'size' ? null : 'size')}
                                className={`flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'size' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                                    <rect x="4.5" y="3" width="17" height="21" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
                                    <circle cx="13" cy="10.5" r="3" stroke="currentColor" strokeWidth="1.6"/>
                                    <path d="M5.5 24c0-4 3.4-6.5 7.5-6.5s7.5 2.5 7.5 6.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                </svg>
                                <span className="text-[9px] font-bold leading-none">ছবির মাপ</span>
                            </button>

                            {/* Background */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'bg' ? null : 'bg')}
                                className={`flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'bg' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                                    <circle cx="13" cy="13" r="9" stroke="currentColor" strokeWidth="1.8"/>
                                    <circle cx="8.5" cy="11" r="2" fill="currentColor" fillOpacity="0.7"/>
                                    <circle cx="17.5" cy="11" r="2" fill="currentColor" fillOpacity="0.7"/>
                                    <circle cx="13" cy="17" r="2" fill="currentColor" fillOpacity="0.7"/>
                                </svg>
                                <span className="text-[9px] font-bold leading-none">ব্যাকগ্রাউন্ড</span>
                            </button>

                            {/* Download FAB */}
                            <div className="flex justify-center">
                                <button
                                    onClick={handleDownload}
                                    disabled={isDownloading || (isDualMode ? (!mergeLeft || !mergeRight) : !previewUrl)}
                                    className="w-14 h-14 -mt-5 rounded-full flex items-center justify-center text-white disabled:opacity-50 border-[3px] border-white dark:border-zinc-900 bg-[linear-gradient(135deg,#22c55e_0%,#16a34a_100%)] shadow-[0_8px_28px_-6px_rgba(22,163,74,0.7)]"
                                >
                                    {isDownloading
                                        ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        : <IconDownload size={22} color="white" />}
                                </button>
                            </div>

                            {/* Adjustments */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'adjust' ? null : 'adjust')}
                                className={`flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'adjust' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                                    <circle cx="8" cy="7" r="2" stroke="currentColor" strokeWidth="1.6"/>
                                    <path d="M3 7h3M10 7h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                    <circle cx="18" cy="13" r="2" stroke="currentColor" strokeWidth="1.6"/>
                                    <path d="M3 13h13M20 13h3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                    <circle cx="10" cy="19" r="2" stroke="currentColor" strokeWidth="1.6"/>
                                    <path d="M3 19h5M12 19h11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                                </svg>
                                <span className="text-[9px] font-bold leading-none">এডজাস্ট</span>
                            </button>

                            {/* Tools */}
                            <button onClick={() => setActiveDrawer(activeDrawer === 'tools' ? null : 'tools')}
                                className={`flex flex-col items-center gap-1 py-0.5 px-0.5 transition-colors ${activeDrawer === 'tools' ? 'text-orange-500' : 'text-zinc-400 dark:text-zinc-500'}`}>
                                <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
                                    <path d="M8 5l-3 7h6L8 5zM8 12v9" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M17 3v6c0 2.2-1.8 4-4 4h0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                                    <path d="M17 21v-8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/>
                                    <circle cx="17" cy="21" r="2" stroke="currentColor" strokeWidth="1.5"/>
                                </svg>
                                <span className="text-[9px] font-bold leading-none">টুলস</span>
                            </button>
                        </div>
                    </nav>

                    {/* ── Photo Size Sheet ── */}
                    <Sheet open={activeDrawer === 'size'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); }}>
                        <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[88vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                            <SheetTitle className="sr-only">Photo Size</SheetTitle>
                                                        <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
                                <div>
                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('imageGeneration.photoSize')}</h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">ছবির মাপ বেছে নিন</p>
                                </div>
                                <button onClick={() => setActiveDrawer(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500"><IconX size={15} /></button>
                            </div>
                            <div className="overflow-y-auto px-3 pb-8" style={{WebkitOverflowScrolling:'touch'}}>
                                {/* Main photo types */}
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-3 mb-3">
                                    <div className={styles.sizeGrid}>
                                        {photoSizes.map((item) => {
                                            const isVisa = item.value === 'bd_visa';
                                            const isSelected = isVisa ? (size === 'bd_visa' || size.startsWith('visa_')) : size === item.value;
                                            return (
                                                <div key={item.value} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:0, overflow:'hidden' }}>
                                                    <div className={`${styles.sizeCard} ${isSelected ? styles.selected : ''}`}
                                                        onClick={() => {
                                                            if (isVisa) { setSize('bd_visa'); setVisaCountryLabel(''); }
                                                            else {
                                                                const newSize = item.value;
                                                                if (newSize === 'dual_image' && previewUrl && size !== 'dual_image') {
                                                                    setMergeLeft({ url: processedUrl || previewUrl, offsetX: 0, offsetY: 0, zoom: 1 });
                                                                    setPreviewUrl(null);
                                                                } else if (newSize !== 'dual_image' && size === 'dual_image') {
                                                                    if (mergeLeft) setPreviewUrl(mergeLeft.url);
                                                                    setMergeLeft(null); setMergeRight(null);
                                                                }
                                                                if (newSize === 'dual_image' || size === 'dual_image') { setProcessedUrl(null); setBgRemoved(false); }
                                                                setAutoEnhanced(false); setPreEnhanceVals(null);
                                                                setSize(newSize); setVisaCountryLabel('');
                                                            }
                                                        }}>
                                                        <div style={{ width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                            <item.icon size={26} strokeWidth={1.5} />
                                                        </div>
                                                        {isSelected && <div className={styles.dressCheckmark}><IconCheck size={10} color='white' strokeWidth={3} /></div>}
                                                    </div>
                                                    <span className={`${styles.sizeCardLabel} ${isSelected ? styles.sizeCardLabelActive : ''}`}>
                                                        {isVisa && visaCountryLabel ? visaCountryLabel : item.abbr}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                        {/* Free size — skips the crop, keeps the image's own aspect */}
                                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4, minWidth:0, overflow:'hidden' }}>
                                            <div className={`${styles.sizeCard} ${size === 'free' ? styles.selected : ''}`} onClick={() => applyPrintSize('free')}>
                                                <div style={{ width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                    <IconArrowsMaximize size={26} strokeWidth={1.5} />
                                                </div>
                                                {size === 'free' && <div className={styles.dressCheckmark}><IconCheck size={10} color='white' strokeWidth={3} /></div>}
                                            </div>
                                            <span className={`${styles.sizeCardLabel} ${size === 'free' ? styles.sizeCardLabelActive : ''}`}>{t('imageManualEditor.freeSize')}</span>
                                        </div>
                                    </div>
                                </div>
                                {/* Visa sizes */}
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-3">
                                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2.5 px-0.5">ভিসার মাপ</p>
                                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                                        {VISA_GROUPS.map((group) => {
                                            const isGroupSelected = size === group.key;
                                            return (
                                                <button key={group.key}
                                                    onClick={() => { setSize(group.key); setVisaCountryLabel(isBn ? group.sizeBn : group.size); }}
                                                    className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-2xl border-2 transition-all text-left ${isGroupSelected ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-700/50'}`}>
                                                    <span className={`text-[12px] font-bold leading-tight ${isGroupSelected ? 'text-orange-500' : 'text-zinc-800 dark:text-zinc-100'}`}>{isBn ? group.sizeBn : group.size}</span>
                                                    <span className="text-[10px] text-zinc-400 dark:text-zinc-500 leading-tight line-clamp-1">{group.countries.slice(0,2).join(', ')}{group.countries.length > 2 ? '...' : ''}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {/* Print / paper sizes */}
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-3 mt-3">
                                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mb-2.5 px-0.5">{isBn ? 'প্রিন্ট মাপ' : 'Print size'}</p>
                                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                                        {PRINT_SIZES.map((p) => {
                                            const isSel = size === p.base;
                                            return (
                                                <button key={p.base}
                                                    onClick={() => applyPrintSize(p.base)}
                                                    className={`flex flex-col items-start gap-0.5 px-3 py-2.5 rounded-2xl border-2 transition-all text-left ${isSel ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20' : 'border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-700/50'}`}>
                                                    <span className={`text-[12px] font-bold leading-tight ${isSel ? 'text-orange-500' : 'text-zinc-800 dark:text-zinc-100'}`}>{p.label}</span>
                                                    <span className="text-[10px] text-zinc-400 dark:text-zinc-500 leading-tight">{p.sub}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* ── Background Sheet ── */}
                    <Sheet open={activeDrawer === 'bg'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); }}>
                        <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[88vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                            <SheetTitle className="sr-only">Background</SheetTitle>
                                                        <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
                                <div>
                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('imageGeneration.background')}</h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">ব্যাকগ্রাউন্ড রং বেছে নিন</p>
                                </div>
                                <button onClick={() => setActiveDrawer(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500"><IconX size={15} /></button>
                            </div>
                            <div className="overflow-y-auto px-3 pb-8" style={{WebkitOverflowScrolling:'touch'}}>
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-4">
                                    <div className={styles.colorGrid} style={{ opacity: (!previewUrl && !isDualMode) || (isDualMode && !mergeLeft && !mergeRight) ? 0.35 : 1, pointerEvents: (!previewUrl && !isDualMode) || (isDualMode && !mergeLeft && !mergeRight) || isRemovingBg ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
                                        {backgrounds.map((item) => (
                                            <div
                                                key={item.id}
                                                className={`${styles.bgColorCard} ${bg === item.id ? styles.selected : ''}`}
                                                onClick={() => handleColorClick(item.id)}
                                                style={{
                                                    background: item.id === 'custom'
                                                        ? (bg === 'custom' ? customColor : 'linear-gradient(135deg,#667eea,#764ba2)')
                                                        : item.id === 'transparent'
                                                            ? 'repeating-conic-gradient(#aaa 0% 25%, #fff 0% 50%) 0 0 / 14px 14px'
                                                            : item.color,
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                }}
                                            >
                                                {item.id === 'custom' ? (
                                                    <>
                                                        <IconPalette size={28} color="white" style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))' }} />
                                                        <input
                                                            type="color"
                                                            value={customColor}
                                                            onChange={(e) => {
                                                                setCustomColor(e.target.value);
                                                                const needsBgRemoval = isDualMode ? !(mergeLeft?.bgRemoved && mergeRight?.bgRemoved) : !bgRemoved;
                                                                if (needsBgRemoval) handleRemoveBg();
                                                            }}
                                                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', width: 0, height: 0 }}
                                                        />
                                                    </>
                                                ) : (
                                                    bg === item.id && (
                                                        <div className={styles.dressCheckmark}>
                                                            <IconCheck size={12} color="white" strokeWidth={3} />
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                    {/* Preset image backgrounds */}
                                    <p className="text-xs font-bold text-zinc-500 dark:text-zinc-400 mt-4 mb-2">{isBn ? 'ছবি ব্যাকগ্রাউন্ড' : 'Image background'}</p>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                                        {BG_PRESETS.map((preset) => {
                                            const sel = parseBgToken(bg).kind === 'preset' && parseBgToken(bg).id === preset.id;
                                            return (
                                                <button
                                                    key={preset.id}
                                                    title={preset.id}
                                                    onClick={() => handlePresetClick(preset)}
                                                    style={{
                                                        width: '100%', aspectRatio: '1', borderRadius: 10, overflow: 'hidden',
                                                        border: sel ? '2px solid #fe9a00' : '2px solid transparent',
                                                        background: `center/cover no-repeat url(${preset.src})`,
                                                        position: 'relative', cursor: 'pointer',
                                                    }}
                                                >
                                                    {sel && <IconCheck size={14} color="#fff" strokeWidth={3} style={{ position: 'absolute', inset: 0, margin: 'auto', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.7))' }} />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {(!previewUrl && !isDualMode) || (isDualMode && !mergeLeft && !mergeRight) ? (
                                        <p className="text-center text-xs text-zinc-400 dark:text-zinc-500 mt-3">ছবি আপলোড করলে ব্যাকগ্রাউন্ড বেছে নিতে পারবেন</p>
                                    ) : null}
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* ── Adjustments Sheet ── */}
                    <Sheet open={activeDrawer === 'adjust'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); }}>
                        <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[88vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                            <SheetTitle className="sr-only">Adjustments</SheetTitle>
                                                        <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
                                <div>
                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">{t('imageManualEditor.imageAdjustments')}</h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">ছবির রং ও আলো ঠিক করুন</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {hasAdjustmentsChanged && (
                                        <button
                                            onClick={() => { setBrightness(100); setContrast(100); setSaturation(100); setSharp(0); }}
                                            className="w-8 h-8 flex items-center justify-center rounded-xl bg-orange-50 dark:bg-orange-900/20 text-orange-500"
                                            title="Reset adjustments"
                                        ><IconRepeat size={14} /></button>
                                    )}
                                    <button onClick={() => setActiveDrawer(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500"><IconX size={15} /></button>
                                </div>
                            </div>
                            {/* Live image preview */}
                            {displayImage && (
                                <div className="mx-3 mb-3 rounded-2xl overflow-hidden flex items-center justify-center" style={{
                                    height: 180,
                                    flexShrink: 0,
                                    ...frameBgStyle(),
                                }}>
                                    <img
                                        src={displayImage}
                                        alt="preview"
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '100%',
                                            objectFit: 'contain',
                                            filter: `brightness(${brightness/100}) contrast(${contrast/100}) saturate(${saturation/100})${sharp > 0 ? ' url(#gim-sharpen)' : ''}`,
                                        }}
                                    />
                                </div>
                            )}
                            <div className="overflow-y-auto px-3 pb-8" style={{WebkitOverflowScrolling:'touch'}}>
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-4">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                                        {[
                                            { label: t('imageManualEditor.brightness'), icon: <IconSun size={14} />, value: brightness, onChange: setBrightness, min: 50, max: 200, isSharp: false },
                                            { label: t('imageManualEditor.contrast'),   icon: <IconContrast size={14} />, value: contrast,   onChange: setContrast,   min: 50, max: 200, isSharp: false },
                                            { label: t('imageManualEditor.saturation'), icon: <IconDroplet size={14} />, value: saturation, onChange: setSaturation, min: 0,  max: 200, isSharp: false },
                                            { label: t('imageGeneration.sharp'),        icon: <IconSparkles size={14} />, value: sharp,      onChange: setSharp,      min: 0,  max: 10,  isSharp: true  },
                                        ].map(({ label, icon, value, onChange, min, max, isSharp }) => (
                                            <div key={label}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
                                                        {icon}{label}
                                                    </span>
                                                    <span className="text-xs font-bold text-orange-500">
                                                        {isSharp ? `${value * 10}%` : `${value >= 100 ? `+${value - 100}` : `${value - 100}`}%`}
                                                    </span>
                                                </div>
                                                <input
                                                    type="range"
                                                    min={min} max={max} value={value}
                                                    onChange={(e) => onChange(Number(e.target.value))}
                                                    className="w-full accent-orange-500"
                                                    disabled={!displayImage}
                                                    style={{ opacity: displayImage ? 1 : 0.35 }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* ── Tools Sheet ── */}
                    <Sheet open={activeDrawer === 'tools'} onOpenChange={(o) => { if (!o) setActiveDrawer(null); }}>
                        <SheetContent side="bottom" className="p-0 rounded-t-[28px] z-[99999] max-h-[88vh] overflow-hidden flex flex-col bg-zinc-100 dark:bg-zinc-900" showClose={false}>
                            <SheetTitle className="sr-only">Tools</SheetTitle>
                                                        <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
                                <div>
                                    <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">টুলস ও অ্যাকশন</h2>
                                    <p className="text-[11px] text-zinc-500 mt-0.5">ডাউনলোড, প্রিন্ট ও এডিট করুন</p>
                                </div>
                                <button onClick={() => setActiveDrawer(null)} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/70 dark:bg-zinc-700 text-zinc-500"><IconX size={15} /></button>
                            </div>
                            <div className="overflow-y-auto px-3 pb-8" style={{WebkitOverflowScrolling:'touch'}}>

                                {/* Action buttons */}
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-4 mb-3">
                                    <p className="text-[11px] font-bold text-zinc-400 dark:text-zinc-500 uppercase mb-3">অ্যাকশন</p>
                                    <div className="flex flex-col gap-2.5">
                                        <button
                                            onClick={handleDownload}
                                            disabled={isDownloading || (isDualMode ? (!mergeLeft || !mergeRight) : !previewUrl)}
                                            className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-colors"
                                            style={{ background: 'rgba(34,197,94,0.12)', color: '#16a34a' }}
                                        >
                                            {isDownloading
                                                ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #16a34a44', borderTopColor: '#16a34a', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                                : <IconDownload size={18} />}
                                            {t('imageManualEditor.download')}
                                        </button>
                                        <button
                                            onClick={handleOpenPrint}
                                            disabled={isPrinting || (isDualMode ? (!mergeLeft || !mergeRight) : !previewUrl)}
                                            className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-colors"
                                            style={{ background: 'rgba(37,99,235,0.12)', color: '#2563eb' }}
                                        >
                                            {isPrinting
                                                ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #2563eb44', borderTopColor: '#2563eb', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                                : <IconPrinter size={18} />}
                                            {t('imageManualEditor.print')}
                                        </button>
                                        <button
                                            onClick={handleSave}
                                            disabled={!(isDualMode ? (mergeLeft && mergeRight) : displayImage) || (!currentEditId && savedToHistory) || isSaving}
                                            className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-50 transition-colors"
                                            style={{ background: 'rgba(230,138,0,0.12)', color: '#e68a00' }}
                                        >
                                            {isSaving
                                                ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #e68a0044', borderTopColor: '#e68a00', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                                : <IconDeviceFloppy size={18} />}
                                            {currentEditId ? t('imageManualEditor.update', 'আপডেট করুন') : (savedToHistory ? t('imageManualEditor.saved') : t('imageManualEditor.save'))}
                                        </button>
                                    </div>
                                </div>

                                {/* Edit tools */}
                                <div className="bg-white dark:bg-zinc-800 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.07)] p-4 mb-3">
                                    <p className="text-[11px] font-bold text-zinc-400 dark:text-zinc-500 uppercase mb-3">{t('imageManualEditor.backgroundRemoval')}</p>
                                    <div className="flex flex-col gap-2.5">
                                        {removeBgButton}
                                        {(displayImage || (isDualMode && (mergeLeft || mergeRight))) && (
                                            <button
                                                className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm"
                                                style={{ background: 'rgba(251,146,60,0.15)', color: '#fb923c', border: '1px solid rgba(251,146,60,0.35)' }}
                                                onClick={() => { setActiveDrawer(null); handleOpenEraserModal(); }}
                                            >
                                                <IconEdit size={18} />
                                                {t('imageManualEditor.eraserOpen')}
                                            </button>
                                        )}

                                        {/* AI Face Enhance (GFPGAN — runs fully in-browser, renders inline in preview) */}
                                        {!isDualMode && displayImage && (
                                            <button
                                                className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm disabled:opacity-50"
                                                style={{ background: 'rgba(124,58,237,0.15)', color: '#a78bfa', border: '1px solid rgba(124,58,237,0.35)' }}
                                                onClick={() => { setActiveDrawer(null); handleFaceEnhance(); }}
                                                disabled={isFaceEnhancing}
                                            >
                                                {isFaceEnhancing
                                                    ? <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #7c3aed44', borderTopColor: '#7c3aed', display: 'inline-block', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                                                    : <IconSparkles size={18} />}
                                                {t('faceEnhance.button')}
                                            </button>
                                        )}

                                        {/* Transparent background */}
                                        {!isDualMode && displayImage && (
                                            <button
                                                className="w-full flex items-center gap-2.5 px-4 py-3 rounded-xl font-semibold text-sm"
                                                style={{ background: 'rgba(148,163,184,0.14)', color: '#64748b', border: `1px solid ${bg === 'transparent' ? '#fe9a00' : 'rgba(148,163,184,0.30)'}` }}
                                                onClick={() => { handleColorClick('transparent'); }}
                                            >
                                                <CheckerIcon id="mtool-transparent-m" size={20} />
                                                {t('imageManualEditor.transparentTool')}
                                            </button>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </SheetContent>
                    </Sheet>
                </>
            )}

            {/* ── Unsaved changes / discard dialog ── */}
            <Dialog open={showDiscardModal} onOpenChange={(open) => {
                if (!open) { setShowDiscardModal(false); pendingNavRef.current = null; }
            }}>
                <DialogContent className="max-w-sm p-0 overflow-hidden">
                    <DialogTitle className="sr-only">{t('imageManualEditor.discardTitle')}</DialogTitle>
                    <div className="p-5 flex flex-col items-center text-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center">
                            <IconX size={22} className="text-orange-500" />
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{t('imageManualEditor.discardTitle')}</p>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{t('imageManualEditor.discardDesc')}</p>
                        </div>
                    </div>
                    <div className="flex border-t border-zinc-100 dark:border-zinc-800">
                        <button
                            type="button"
                            onClick={() => setShowDiscardModal(false)}
                            className="flex-1 py-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors border-r border-zinc-100 dark:border-zinc-800"
                        >
                            {t('imageManualEditor.discardStay')}
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowDiscardModal(false);
                                pendingNavRef.current?.();
                                pendingNavRef.current = null;
                            }}
                            className="flex-1 py-3 text-sm font-semibold text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                        >
                            {t('imageManualEditor.discardLeave')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Crop / rotate stage — full-screen, shown on every fresh upload before the
                image lands in the canvas (same component the AI editor uses). Portaled to
                <body> so it overlays regardless of the page's stacking contexts. */}
            {showCropModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'var(--color-body, #ffffff)', display: 'flex', flexDirection: 'column' }}>
                    <CropStage
                        ref={cropStageRef}
                        imageSrc={cropImageSrc}
                        fileName={pendingCropName}
                        isMobile={isMobile}
                        t={t}
                        aspect={cropCfg.ratio}
                        outWidth={cropCfg.outW}
                        outHeight={cropCfg.outH}
                        // Keep every pixel of the crop: this output is the photo the
                        // user prints, not an AI upload. 8MB of budget sits under the
                        // manual-edit route's 10MB limit, so the JPEG stays at q0.95
                        // instead of stepping down to fit.
                        outMaxDim={MAX_SOURCE_DIM}
                        maxBytes={8 * 1024 * 1024}
                        hint=""
                        showSkip
                        onClose={closeCropModal}
                        onApply={handleCropApply}
                        onSkip={handleCropSkip}
                        onError={handleCropError}
                    />
                </div>,
                document.body,
            )}
        </>
    );
}
